<template>
  <div class="admin-notification-page">
    <div class="notification-page-header">
      <div>
        <h1>Toplu Bildirim Gönder</h1>
        <p>
          Kime, hangi kanaldan, hangi mesajın gideceğini tek akışta kontrol
          edin.
        </p>
      </div>
      <div class="notification-page-header__actions">
        <v-btn text @click="saveDraft">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Taslak Kaydet
        </v-btn>
        <v-btn text @click="clearDraft">
          <v-icon left small>mdi-refresh</v-icon>
          Temizle
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSending"
          :disabled="!isMessageReady || !hasSelectedRecipients"
          @click="handleSendClick"
        >
          <v-icon left small>mdi-send</v-icon>
          Gönder
        </v-btn>
      </div>
    </div>

    <v-row dense class="mb-3">
      <v-col cols="12" md="4">
        <v-card
          class="step-card"
          :class="stepCardClass('message')"
          @click="goToStep('message')"
        >
          <v-card-text>
            <div class="step-card__number">1</div>
            <div>
              <div class="step-card__title">Mesaj</div>
              <div class="step-card__text">{{ messageStepText }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card
          class="step-card"
          :class="stepCardClass('target')"
          @click="goToStep('target')"
        >
          <v-card-text>
            <div class="step-card__number">2</div>
            <div>
              <div class="step-card__title">Hedef Kitle</div>
              <div class="step-card__text">{{ targetingStatusText }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="4">
        <v-card
          class="step-card"
          :class="stepCardClass('control')"
          @click="goToStep('control')"
        >
          <v-card-text>
            <div class="step-card__number">3</div>
            <div>
              <div class="step-card__title">Kontrol</div>
              <div class="step-card__text">{{ deliveryCheckText }}</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12">
        <v-card
          v-if="currentStep === 'message'"
          class="mb-4 wizard-panel message-step-card"
        >
          <div class="message-card-header">
            <div>
              <div class="message-card-header__title">Mesaj Bilgisi</div>
              <div class="message-card-header__subtitle">
                Gönderim kanalı değiştiğinde uygunluk kontrolü yeniden
                hesaplanır.
              </div>
            </div>
            <v-btn text color="primary" @click="selectTemplate">
              Şablon Seç
            </v-btn>
          </div>
          <v-card-text class="message-card-body">
            <v-form ref="sendForm" @submit.prevent="handleSendClick">
              <v-row class="message-form-grid">
                <v-col cols="12" md="6" class="message-form-grid__field">
                  <label class="section-label">Kanal</label>
                  <v-btn-toggle
                    v-model="formData.notification_channel_id"
                    class="channel-toggle"
                    mandatory
                    dense
                  >
                    <v-btn
                      v-for="channel in channelOptions"
                      :key="channel.id"
                      :value="channel.id"
                      small
                    >
                      {{ channel.name || channel.text }}
                    </v-btn>
                  </v-btn-toggle>
                </v-col>
                <v-col
                  v-if="currentDeliveryProviderOptions.length > 0"
                  cols="12"
                  md="6"
                  class="message-form-grid__field"
                >
                  <rs-select
                    label="Servis Sağlayıcı"
                    v-model="formData.delivery_provider"
                    :items="currentDeliveryProviderOptions"
                    item-text="text"
                    item-value="id"
                    :loading="isDeliveryProvidersLoading"
                    :disabled="currentDeliveryProviderOptions.length === 1"
                    outlined
                    dense
                  />
                  <div
                    v-if="shouldShowProviderCredit"
                    class="provider-credit"
                    :class="providerCreditClass"
                  >
                    <v-icon small>{{ providerCreditIcon }}</v-icon>
                    <span>{{ providerCreditText }}</span>
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="message-form-grid__field">
                  <rs-text-field
                    label="Başlık (Opsiyonel)"
                    v-model="formData.subject"
                    :rules="[rules.maxLength(formData.subject, 120)]"
                    hint="E-posta ve mobil için kullanılır"
                    persistent-hint
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6" class="message-form-grid__field">
                  <rs-select
                    label="Gönderim zamanı"
                    v-model="sendTiming"
                    :items="sendTimingOptions"
                    item-text="text"
                    item-value="id"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" class="message-form-grid__field">
                  <rs-textarea
                    label="Mesaj"
                    v-model="formData.body"
                    :rules="[
                      rules.required,
                      rules.maxLength(formData.body, 1000),
                    ]"
                    rows="5"
                    required
                    outlined
                    dense
                  />
                  <div
                    v-if="messageInsightItems.length"
                    class="message-insights"
                  >
                    <div
                      v-for="item in messageInsightItems"
                      :key="item.key"
                      class="message-insight"
                      :class="`message-insight--${item.type}`"
                    >
                      <v-icon small>{{ item.icon }}</v-icon>
                      <div>
                        <strong>{{ item.title }}</strong>
                        <span>{{ item.text }}</span>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions class="wizard-actions justify-end">
            <v-btn
              color="primary"
              :disabled="!isMessageReady"
              @click="goToStep('target')"
            >
              Hedef Kitleye Geç
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card
          v-if="currentStep === 'target'"
          class="mb-4 wizard-panel section-panel"
        >
          <div class="section-panel-header">
            <div>
              <div class="section-panel-header__title">Hedef Kitle</div>
              <div class="section-panel-header__subtitle">
                Kapsamı ve alıcı tipini seçin; kriterler seçilen kapsam içinde
                uygulanır.
              </div>
            </div>
            <div v-if="isMessageReady" class="delivery-context-chip">
              <v-icon small>mdi-message-text-outline</v-icon>
              <span>{{ deliverySelectionLabel }}</span>
            </div>
          </div>
          <v-card-text class="target-panel-body">
            <div class="target-builder">
              <div class="target-builder__row">
                <div class="target-builder__label">
                  <strong>1. Alıcı Tipi</strong>
                  <span>Kime gönderilecek?</span>
                </div>
                <div class="target-builder__control">
                  <v-btn-toggle
                    v-model="segment.recipient_type"
                    mandatory
                    dense
                    class="scope-toggle"
                    @change="handleRecipientTypeChanged"
                  >
                    <v-btn
                      v-for="recipientType in recipientTypeOptions"
                      :key="recipientType.id"
                      :value="recipientType.id"
                      :title="recipientType.description"
                      small
                    >
                      {{ recipientType.text }}
                    </v-btn>
                  </v-btn-toggle>
                  <div class="target-note">
                    {{ selectedRecipientTypeDescription }}
                  </div>
                </div>
              </div>

              <div class="target-builder__row">
                <div class="target-builder__label">
                  <strong>2. Kapsam</strong>
                  <span>Hangi kayıtlarda aranacak?</span>
                </div>
                <div class="target-builder__control">
                  <v-btn-toggle
                    v-model="sourcePreset"
                    mandatory
                    dense
                    class="scope-toggle"
                    @change="handleSourcePresetChanged"
                  >
                    <v-btn
                      v-for="source in availableSourcePresetOptions"
                      :key="source.id"
                      :value="source.id"
                      small
                    >
                      {{ source.text }}
                    </v-btn>
                  </v-btn-toggle>

                  <div class="target-note">
                    {{ sourcePresetNote }}
                  </div>
                </div>
              </div>

              <div class="target-builder__row">
                <div class="target-builder__label">
                  <strong>3. Kriterler</strong>
                  <span>Kriterler ekleyerek daraltın.</span>
                </div>
                <div class="target-builder__control">
                  <div class="criteria-row">
                    <div
                      v-if="selectedCriteria.length === 0"
                      class="criteria-empty"
                    >
                      Herhangi bir kriter seçilmedi. Varsayılan olarak aktif
                      kayıtlar kullanılır.
                    </div>
                    <div v-else class="selected-criteria">
                      <div
                        v-for="criterion in selectedCriterionDefinitions"
                        :key="criterion.key"
                        class="selected-criterion"
                      >
                        <div>
                          <strong>{{ criterion.label }}</strong>
                          <span>{{ criterion.description }}</span>
                        </div>
                        <v-btn
                          icon
                          small
                          @click="removeCriterion(criterion.key)"
                        >
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <v-btn color="primary" text @click="openCriteriaDrawer">
                      <v-icon left small>mdi-filter-plus-outline</v-icon>
                      Kriter Ekle
                    </v-btn>
                  </div>
                </div>
              </div>

              <div class="target-builder__row">
                <div class="target-builder__label">
                  <strong>4. Filtreler</strong>
                  <span>Genel filtreler ekleyin</span>
                </div>
                <div class="target-builder__control">
                  <v-row dense>
                    <v-col cols="12" md="6" v-if="sourcePreset === 'customers'">
                      <rs-select-customer
                        label="Müşteri"
                        v-model="search.customer_ids"
                        multiple
                        clearable
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                      v-if="sourcePreset === 'management_companies'"
                    >
                      <rs-autocomplete-company
                        :value="search.company_ids"
                        label="Yönetim Şirketleri"
                        multiple
                        @change:option="handleCompanySelected"
                        clearable
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                      v-if="sourcePreset === 'selected_clusters'"
                    >
                      <rs-autocomplete-company
                        :value="search.company_ids"
                        label="Yönetim Şirketleri"
                        multiple
                        @change:option="handleCompanySelected"
                        clearable
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      md="6"
                      v-if="sourcePreset === 'selected_clusters'"
                    >
                      <rs-autocomplete-cluster
                        :value="search.cluster_ids"
                        :company-ids="search.company_ids"
                        label="TYA'lar"
                        multiple
                        @change:option="handleClusterSelected"
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <rs-select
                        label="Rol"
                        v-model="search.role_ids"
                        :items="roleList"
                        item-text="display_name"
                        item-value="id"
                        multiple
                        clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <rs-text-field
                        label="Genel Arama"
                        v-model="search.query"
                      />
                    </v-col>
                  </v-row>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="target-actions">
            <div class="text--secondary text-caption">
              {{ recipientActionSummary }}
            </div>
            <div>
              <v-btn text @click="resetFilters">Filtreleri Temizle</v-btn>
            </div>
          </v-card-actions>
        </v-card>

        <v-card
          v-if="currentStep === 'target'"
          class="mb-4 wizard-panel section-panel"
        >
          <div class="section-panel-header">
            <div>
              <div class="section-panel-header__title">Filtre Sonuçları</div>
              <div class="section-panel-header__subtitle">
                Filtreye uyan kullanıcıları seçip gönderim listesine ekleyin.
              </div>
            </div>
            <div class="section-panel-header__actions">
              <v-btn
                text
                :disabled="tableSelectedUserIds.length === 0"
                @click="addSelectedUsersToTemp"
              >
                Seçilenleri Ekle
              </v-btn>
              <v-btn
                color="primary"
                text
                :loading="isBulkAdding"
                :disabled="isBulkAdding || !hasRecipientScopeSelection"
                @click="addAllFilteredUsers"
              >
                Tümünü Ekle
              </v-btn>
              <v-btn text v-if="isBulkAdding" @click="cancelBulkAdd">
                İptal
              </v-btn>
            </div>
          </div>
          <v-card-text>
            <v-alert
              v-if="!hasRecipientScopeSelection"
              type="info"
              border="left"
              dense
              class="mb-2"
            >
              Kullanıcıları listelemek için hedef kapsamı tamamlayın.
            </v-alert>
            <v-data-table
              v-bind="notificationDataTableAttrs"
              class="recipient-table"
              :headers="headersShown"
              :items="list"
              :loading="isLoading"
              :options="options"
              :server-items-length="total"
              v-model="selectedItems"
              @update:options="handleTableOptionsUpdated"
            >
              <template v-slot:top>
                <rs-table-title-bar
                  v-if="$refs.headSearch"
                  title="Eşleşen Kullanıcılar"
                  icon="mdi-account-search"
                  add-route="#"
                  :add-enabled="tableSelectedUserIds.length > 0"
                  @clear-filters="$refs.headSearch.reset"
                  @click:add="addSelectedUsersToTemp"
                  @reload="loadList"
                  hide-edit
                  :search.sync="search.query"
                  v-bind="titleBarAttrs"
                ></rs-table-title-bar>
              </template>

              <template v-slot:body.prepend="{ headers }">
                <rs-table-head-search
                  :headers="headers"
                  :search.sync="search"
                  ref="headSearch"
                />
              </template>

              <template v-slot:body.append="{ headers }">
                <rs-table-foot-totals
                  :headers="headers"
                  :totals="footTotals"
                  v-if="list.length > 0"
                />
              </template>

              <template v-slot:item.name="{ item }">
                <div class="recipient-name-cell">
                  <strong>{{ item.name || "#" + item.id }}</strong>
                  <span
                    v-if="primaryRelationGroup(item)"
                    :title="formatRelationGroup(primaryRelationGroup(item))"
                  >
                    {{ primaryRelationGroup(item).label }}:
                    {{ formatRelationGroup(primaryRelationGroup(item)) }}
                  </span>
                </div>
              </template>

              <template v-slot:item.phone="{ value }">
                <rs-table-cell-phone :value="value" />
              </template>

              <template v-slot:item.email="{ value }">
                <rs-table-cell-email :value="value" />
              </template>

              <template v-slot:item.roles="{ value }">
                <span
                  v-if="roleLabels(value).length > 0"
                  class="role-summary"
                  :title="roleLabels(value).join(', ')"
                >
                  {{ formatRoleSummary(value) }}
                </span>
                <span v-else class="text--disabled">-</span>
              </template>
            </v-data-table>
          </v-card-text>
          <v-card-actions class="wizard-actions">
            <v-btn text @click="goToStep('message')">Mesaja Dön</v-btn>
            <v-spacer />
            <div class="text--secondary text-caption mr-3">
              {{ recipientActionSummary }}
            </div>
            <v-btn
              color="primary"
              :disabled="!hasRecipientScopeSelection || !hasSelectedRecipients"
              @click="goToStep('control')"
            >
              Kontrole Geç
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card
          v-if="currentStep === 'control'"
          class="send-control-card wizard-panel section-panel"
        >
          <div class="section-panel-header">
            <div>
              <div class="section-panel-header__title">Gönderim Kontrolü</div>
              <div class="section-panel-header__subtitle">
                Yanlış kitleye gönderimi engelleyen son özet.
              </div>
            </div>
            <div v-if="isMessageReady" class="delivery-context-chip">
              <v-icon small>mdi-message-text-outline</v-icon>
              <span>{{ deliverySelectionLabel }}</span>
            </div>
          </div>
          <v-card-text class="control-panel-body">
            <v-progress-linear
              v-if="isBulkAdding"
              :value="bulkAddProgress"
              height="6"
              class="mb-3"
            />

            <div
              v-if="
                tempSelectedItems.length === 0 &&
                selectedSegmentSelections.length === 0
              "
              class="text--secondary"
            >
              Henüz kullanıcı seçilmedi.
            </div>

            <template v-else>
              <div class="control-final-summary" :class="controlDecisionClass">
                <div>
                  <h3>{{ controlDecisionTitle }}</h3>
                  <p>{{ controlDecisionText }}</p>
                </div>
                <div class="control-final-count">
                  {{ controlSendableCount }}
                  <span>{{ controlFinalCountLabel }}</span>
                </div>
              </div>

              <div class="control-delivery-context">
                <div>
                  <span>Kanal</span>
                  <strong>{{ selectedChannelLabel }}</strong>
                </div>
                <div>
                  <span>Servis</span>
                  <strong>{{ selectedProviderLabel }}</strong>
                </div>
                <div>
                  <span>Mesaj</span>
                  <strong>{{ messageLengthSummary }}</strong>
                </div>
                <div v-if="shouldShowProviderCredit">
                  <span>Kredi</span>
                  <strong>{{ providerCreditText }}</strong>
                </div>
              </div>

              <div class="control-state-strip">
                <div class="control-state-card" :class="channelStateClass">
                  <div>
                    <strong>{{ channelStateTitle }}</strong>
                    <span>{{ channelStateText }}</span>
                  </div>
                  <v-btn
                    v-if="hasInvalidRecipients"
                    text
                    small
                    :loading="isRemovingInvalid"
                    :disabled="isRemovingInvalid || isBulkAdding"
                    @click="removeInvalidRecipients"
                  >
                    Uygun Olmayanları Çıkar
                  </v-btn>
                </div>

                <div class="control-state-card">
                  <div>
                    <strong>{{ dedupeControlTitle }}</strong>
                    <span>{{ dedupeHelpText }}</span>
                  </div>
                  <v-switch
                    v-model="isDedupeEnabled"
                    inset
                    dense
                    hide-details
                    class="control-dedupe-switch"
                  />
                </div>
              </div>

              <div class="control-stat-grid">
                <div class="control-stat">
                  <span>Toplam hedef</span>
                  <strong>{{ controlTotalTargetCount }}</strong>
                </div>
                <div class="control-stat control-stat--out">
                  <span>Liste dışında</span>
                  <strong>{{ controlExcludedCount }}</strong>
                </div>
                <div class="control-stat control-stat--final">
                  <span>{{ controlSendableLabel }}</span>
                  <strong>{{ controlSendableCount }}</strong>
                </div>
              </div>

              <div class="control-simple-checks">
                <div
                  class="control-simple-check"
                  :class="channelSimpleCheckClass"
                >
                  <strong>{{ validationWarningTitle }}</strong>
                  <span>{{ validationWarningText }}</span>
                </div>
                <div class="control-simple-check">
                  <strong>Filtreler bağlı</strong>
                  <span>{{ backendLogicSummary }}</span>
                </div>
                <div
                  class="control-simple-check"
                  :class="messageSimpleCheckClass"
                >
                  <strong>{{ messageSafetyTitle }}</strong>
                  <span>{{ messageSafetyText }}</span>
                </div>
                <div
                  class="control-simple-check"
                  :class="dedupeSimpleCheckClass"
                >
                  <strong>Tekilleştirme kontrolü</strong>
                  <span>{{ dedupeHelpText }}</span>
                </div>
              </div>
            </template>

            <div
              v-if="selectedSegmentSelections.length > 0"
              class="control-list-note"
            >
              <div>
                Listeye eklenen:
                <strong>{{ selectedSegmentSelections.length }}</strong>
                hedef kitle, Toplam
                <strong>{{ selectedSegmentTotal }}</strong>
                <span v-if="controlExcludedCount">
                  , Çıkarılan {{ controlExcludedCount }} kullanıcı var.
                </span>
                Alıcı listesi:
                <strong>{{ controlSendableCount }}</strong>
                alıcı.
              </div>
              <div class="control-list-note__actions">
                <v-btn text small @click="openTempListDialog">
                  Listeyi Görüntüle
                </v-btn>
                <v-btn text small @click="clearSelectedUsers">
                  Listeyi Temizle
                </v-btn>
              </div>
            </div>

            <div v-if="tempSelectedItems.length > 0" class="control-chip-list">
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="user in tempListPreview"
                  :key="user.id"
                  class="ma-1"
                  color="primary"
                  text-color="white"
                  close
                  small
                  :title="user.email || user.phone || ''"
                  @click:close="removeSelectedUser(user.id)"
                >
                  {{ user.name || user.email || user.phone || "#" + user.id }}
                </v-chip>
              </div>
              <div v-if="tempListOverflowCount > 0" class="text--secondary">
                ve {{ tempListOverflowCount }} kişi daha
              </div>
              <div class="text-right mt-2">
                <v-btn
                  text
                  small
                  :disabled="tempSelectedItems.length === 0"
                  @click="openTempListDialog"
                >
                  Listeyi Görüntüle
                </v-btn>
                <v-btn
                  text
                  small
                  :disabled="!hasSelectedRecipients"
                  @click="clearSelectedUsers"
                >
                  Listeyi Temizle
                </v-btn>
              </div>
            </div>

            <v-switch
              v-model="showAdvancedTools"
              label="Geliştirilmiş araçlar"
              inset
              class="mt-3"
            />
            <div v-if="showAdvancedTools">
              <rs-textarea
                label="Kullanıcı ID listesi / JSON"
                v-model="manualUserIdInput"
                rows="2"
                hint="ID listesi veya user JSON çıktısı"
                persistent-hint
              />
              <div class="text-right">
                <v-btn
                  text
                  small
                  :disabled="!manualUserIdInput.trim()"
                  @click="addManualUserIds"
                >
                  ID'leri Ekle
                </v-btn>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="wizard-actions">
            <v-btn text @click="goToStep('target')">Hedef Kitleye Dön</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              :loading="isSending"
              :disabled="!isMessageReady || !hasSelectedRecipients"
              @click="handleSendClick"
            >
              Kontrol Et ve Gönder
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <v-navigation-drawer
      v-model="criteriaDrawer"
      right
      temporary
      fixed
      width="440"
      class="criteria-drawer"
    >
      <div class="criteria-drawer__header">
        <div>
          <strong>Kriter Ekle</strong>
          <span>{{ selectedRecipientTypeText }} için uygun kriterler</span>
        </div>
        <v-btn icon @click="criteriaDrawer = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <div class="criteria-drawer__body">
        <rs-text-field
          label="Kriter ara"
          v-model="criteriaSearch"
          class="criteria-search"
          clearable
          outlined
          dense
        />

        <div class="criteria-categories">
          <v-btn
            v-for="category in criteriaCategories"
            :key="category.id"
            small
            text
            :class="{
              'criteria-category--active':
                activeCriteriaCategory === category.id,
            }"
            @click="activeCriteriaCategory = category.id"
          >
            {{ category.text }}
          </v-btn>
        </div>

        <v-progress-linear
          v-if="isCriteriaLoading"
          indeterminate
          height="3"
          class="mb-3"
        />

        <div
          v-if="filteredCriteriaDefinitions.length === 0"
          class="criteria-empty"
        >
          Bu seçim için uygun kriter bulunamadı.
        </div>

        <div v-else class="criteria-list">
          <div
            v-for="criterion in filteredCriteriaDefinitions"
            :key="criterion.key"
            class="criterion-option"
            :class="{
              'criterion-option--selected': isCriterionSelected(criterion.key),
            }"
          >
            <div>
              <strong>{{ criterion.label }}</strong>
              <span>{{ criterion.description }}</span>
            </div>
            <v-btn
              small
              :color="
                isCriterionSelected(criterion.key) ? 'success' : 'primary'
              "
              text
              class="criterion-option__action"
              :class="{
                'criterion-option__action--selected': isCriterionSelected(
                  criterion.key
                ),
              }"
              :disabled="isCriterionSelected(criterion.key)"
              @click="addCriterion(criterion)"
            >
              {{ isCriterionSelected(criterion.key) ? "Eklendi" : "Ekle" }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-navigation-drawer>

    <rs-confirm
      ref="confirmSend"
      :loading="isSending"
      @confirmed="handleSendConfirmed"
      @cancelled="$refs.confirmSend.hide()"
    />

    <v-dialog v-model="tempListDialog" max-width="900">
      <v-card v-if="tempListDialog">
        <v-card-title>
          Listeye Ekli Kullanicilar
          <span class="text--secondary ml-2">
            ({{
              hasSnapshotSelection
                ? selectionPreviewTotal
                : tempSelectedItems.length
            }})
          </span>
        </v-card-title>
        <v-card-text>
          <v-row v-if="!hasSnapshotSelection" dense class="align-center mb-2">
            <v-col cols="12" sm="6">
              <v-switch
                v-model="showOnlyDuplicates"
                label="Sadece Tekil Olmayan Kayıtları Goster"
                inset
              />
            </v-col>
            <v-col cols="12" sm="6" class="text-right">
              <div
                v-if="showOnlyDuplicates && !isDedupeSupported"
                class="text--secondary"
              >
                Bu kanal icin tekil olmayan kayıt yok.
              </div>
              <div
                v-else-if="
                  showOnlyDuplicates && duplicateSummary.groupCount === 0
                "
                class="text--secondary"
              >
                Tekil olmayan kayıt bulunamadi.
              </div>
              <div v-else-if="showOnlyDuplicates" class="text--secondary">
                {{ duplicateSummary.groupCount }} grup,
                {{ duplicateSummary.duplicateCount }} tekrar eden kayıt
              </div>
            </v-col>
          </v-row>
          <rs-text-field
            v-if="!showOnlyDuplicates || !isDedupeSupported"
            label="Ara"
            v-model="tempListQuery"
            clearable
          />
          <v-data-table
            v-if="hasSnapshotSelection"
            class="recipient-table"
            :headers="tempListHeaders"
            :items="selectionPreviewItems"
            :loading="isSelectionPreviewLoading"
            :server-items-length="selectionPreviewTotal"
            :items-per-page.sync="selectionPreviewPerPage"
            :page.sync="selectionPreviewPage"
            dense
            :mobile-breakpoint="0"
            @update:page="handleSelectionPreviewPageUpdated"
            @update:items-per-page="handleSelectionPreviewItemsPerPageUpdated"
          >
            <template v-slot:item.name="{ item }">
              <div class="recipient-name-cell">
                <strong>{{ item.name || "#" + item.id }}</strong>
                <span
                  v-if="primaryRelationGroup(item)"
                  :title="formatRelationGroup(primaryRelationGroup(item))"
                >
                  {{ primaryRelationGroup(item).label }}:
                  {{ formatRelationGroup(primaryRelationGroup(item)) }}
                </span>
              </div>
            </template>
            <template v-slot:item.phone="{ value }">
              <rs-table-cell-phone :value="value" />
            </template>
            <template v-slot:item.email="{ value }">
              <rs-table-cell-email :value="value" />
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon @click="removeSelectedUser(item.id)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <div v-else-if="showOnlyDuplicates && isDedupeSupported">
            <v-card
              v-for="group in duplicateGroups"
              :key="group.key"
              class="mb-3"
              outlined
            >
              <v-card-title class="text-subtitle-2">
                {{ group.label }} ({{ group.items.length }})
              </v-card-title>
              <v-card-text>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">Adı</th>
                      <th class="text-left">E-posta</th>
                      <th class="text-left">Telefon</th>
                      <th class="text-right">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in group.items" :key="item.id">
                      <td>{{ item.name || "#" + item.id }}</td>
                      <td>{{ item.email || "-" }}</td>
                      <td>{{ item.phone || "-" }}</td>
                      <td class="text-right">
                        <v-btn
                          x-small
                          text
                          @click="keepOnlyUserInGroup(group.key, item.id)"
                        >
                          Bunu Bırak
                        </v-btn>
                        <v-btn
                          x-small
                          text
                          @click="removeSelectedUser(item.id)"
                        >
                          Çıkar
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-card-text>
            </v-card>
          </div>
          <v-data-table
            v-else
            class="recipient-table"
            :headers="tempListHeaders"
            :items="tempListItems"
            :items-per-page.sync="tempListPerPage"
            :page.sync="tempListPage"
            dense
            :mobile-breakpoint="0"
          >
            <template v-slot:body.prepend="{ headers }">
              <rs-table-head-search
                v-if="!showOnlyDuplicates"
                :headers="headers"
                :search.sync="tempListSearch"
                ref="tempHeadSearch"
              />
            </template>
            <template v-slot:item.name="{ item }">
              <div class="recipient-name-cell">
                <strong>{{ item.name || "#" + item.id }}</strong>
                <span
                  v-if="primaryRelationGroup(item)"
                  :title="formatRelationGroup(primaryRelationGroup(item))"
                >
                  {{ primaryRelationGroup(item).label }}:
                  {{ formatRelationGroup(primaryRelationGroup(item)) }}
                </span>
              </div>
            </template>
            <template v-slot:item.phone="{ value }">
              <rs-table-cell-phone :value="value" />
            </template>
            <template v-slot:item.email="{ value }">
              <rs-table-cell-email :value="value" />
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon @click="removeSelectedUser(item.id)">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="tempListDialog = false">Kapat</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import validation from "@/core/validation";
import { debounce } from "@/core/plugins/debounce";
import { mapGetters } from "vuex";
import { filtersToURL } from "@/view/mixins";

export default {
  name: "AdminNotificationPage",
  mixins: [filtersToURL],
  computed: {
    ...mapGetters(["notificationChannelListIds", "roleList"]),
    notificationDataTableAttrs() {
      return {
        ...this.dataTableAttrs,
        footerProps: {
          ...(this.dataTableAttrs.footerProps || {}),
          itemsPerPageOptions: [10, 20, 50, 100, 200, 500, 1000, 2000],
        },
      };
    },
    channelOptions() {
      return this.notificationChannelListIds.filter((item) =>
        [2, 4, 5].includes(item.id)
      );
    },
    currentDeliveryProviderGroup() {
      if (!this.formData.notification_channel_id) {
        return null;
      }

      return (
        this.deliveryProviderGroups.find(
          (group) => group.channel_id === this.formData.notification_channel_id
        ) || null
      );
    },
    currentDeliveryProviderOptions() {
      return (this.currentDeliveryProviderGroup?.providers || []).map(
        (provider) => ({
          ...provider,
          text: provider.name || provider.text || provider.id,
        })
      );
    },
    currentDeliveryProviderOption() {
      return (
        this.currentDeliveryProviderOptions.find(
          (provider) => provider.id === this.formData.delivery_provider
        ) || null
      );
    },
    selectedChannelKey() {
      if (this.currentDeliveryProviderGroup?.channel) {
        return this.currentDeliveryProviderGroup.channel;
      }

      const map = {
        2: "sms",
        4: "mail",
        5: "mobile",
      };

      return map[this.formData.notification_channel_id] || null;
    },
    selectedChannelLabel() {
      if (this.currentDeliveryProviderGroup?.channel_name) {
        return this.currentDeliveryProviderGroup.channel_name;
      }

      const channel = this.notificationChannelListIds.find(
        (item) => item.id === this.formData.notification_channel_id
      );

      return channel?.name || channel?.text || "Kanal seçilmedi";
    },
    selectedProviderLabel() {
      if (this.currentDeliveryProviderOption) {
        return this.currentDeliveryProviderOption.text;
      }

      return this.formData.delivery_provider || "Servis seçilmedi";
    },
    deliverySelectionLabel() {
      if (!this.formData.notification_channel_id) {
        return "Kanal seçilmedi";
      }

      return `${this.selectedChannelLabel} / ${this.selectedProviderLabel}`;
    },
    messageStepText() {
      if (!this.formData.notification_channel_id) {
        return "Kanal bekliyor";
      }

      if (!this.formData.body) {
        return `${this.selectedChannelLabel} için içerik bekliyor`;
      }

      return this.deliverySelectionLabel;
    },
    isMessageReady() {
      return !!this.formData.notification_channel_id && !!this.formData.body;
    },
    isSmsChannel() {
      return (
        this.selectedChannelKey === "sms" ||
        this.formData.notification_channel_id === 2
      );
    },
    isPushChannel() {
      return (
        this.selectedChannelKey === "mobile" ||
        this.formData.notification_channel_id === 5
      );
    },
    isEmailChannel() {
      return (
        this.selectedChannelKey === "mail" ||
        this.formData.notification_channel_id === 4
      );
    },
    shouldShowProviderCredit() {
      return this.isSmsChannel && !!this.formData.delivery_provider;
    },
    providerCreditText() {
      if (!this.shouldShowProviderCredit) {
        return "Kredi bilgisi yok";
      }

      if (this.isProviderCreditLoading) {
        return "Kredi sorgulanıyor";
      }

      if (this.providerCredit?.available) {
        return `${this.formatNumber(this.providerCredit.credits)} SMS kredisi`;
      }

      return this.providerCredit?.message || "Kredi bilgisi alınamadı";
    },
    providerCreditClass() {
      return {
        "provider-credit--ok": this.providerCredit?.available,
        "provider-credit--warn":
          !this.isProviderCreditLoading && !this.providerCredit?.available,
      };
    },
    providerCreditIcon() {
      if (this.isProviderCreditLoading) {
        return "mdi-progress-clock";
      }

      return this.providerCredit?.available
        ? "mdi-wallet-outline"
        : "mdi-alert-outline";
    },
    smsMessageMetrics() {
      const body = this.formData.body || "";
      const isGsm7 = this.isGsm7Text(body);
      const characters = isGsm7
        ? this.countGsm7Characters(body)
        : Array.from(body).length;
      const singleLimit = isGsm7 ? 160 : 70;
      const concatLimit = isGsm7 ? 153 : 67;
      const segmentCount =
        characters <= singleLimit ? 1 : Math.ceil(characters / concatLimit);

      return {
        characters,
        encoding: isGsm7 ? "GSM-7" : "Unicode",
        segmentCount: body ? segmentCount : 0,
      };
    },
    messageLengthSummary() {
      const body = this.formData.body || "";

      if (!body) {
        return "Mesaj yok";
      }

      if (this.isSmsChannel) {
        const metrics = this.smsMessageMetrics;

        return `${metrics.characters} karakter, ${metrics.segmentCount} SMS`;
      }

      if (this.isPushChannel) {
        return `${Array.from(body).length} karakter push`;
      }

      if (this.isEmailChannel) {
        return `${Array.from(body).length} karakter e-posta`;
      }

      return `${Array.from(body).length} karakter`;
    },
    messageWarningItems() {
      const body = this.formData.body || "";
      const subject = this.formData.subject || "";
      const items = [];

      if (!body) {
        return items;
      }

      if (this.isSmsChannel && this.smsMessageMetrics.encoding === "Unicode") {
        items.push({
          key: "unicode-sms",
          type: "warn",
          icon: "mdi-alert-outline",
          title: "Unicode SMS",
          text: "Türkçe veya özel karakterler SMS segmentini 70/67 karakter üzerinden hesaplatabilir.",
        });
      }

      if (this.isSmsChannel && this.smsMessageMetrics.segmentCount > 3) {
        items.push({
          key: "long-sms",
          type: "warn",
          icon: "mdi-alert-outline",
          title: "Uzun SMS",
          text: `${this.smsMessageMetrics.segmentCount} segment oluşuyor; maliyet ve okunabilirliği kontrol edin.`,
        });
      }

      if (this.containsUrl(body)) {
        items.push({
          key: "contains-url",
          type: "warn",
          icon: "mdi-link-variant",
          title: "Link içeriyor",
          text: "Linkli mesajlarda spam filtresi ve kısa link güveni ayrıca kontrol edilmeli.",
        });
      }

      if (this.hasAggressivePunctuation(body)) {
        items.push({
          key: "punctuation",
          type: "warn",
          icon: "mdi-alert-outline",
          title: "Agresif noktalama",
          text: "Üç veya daha fazla ünlem/soru işareti spam algısını artırabilir.",
        });
      }

      if (this.isMostlyUppercase(body)) {
        items.push({
          key: "uppercase",
          type: "warn",
          icon: "mdi-format-letter-case",
          title: "Yoğun büyük harf",
          text: "Tamamı büyük harfe yakın metinler spam/rahatsız edici algılanabilir.",
        });
      }

      if (this.isPushChannel && subject.length > 50) {
        items.push({
          key: "push-title",
          type: "warn",
          icon: "mdi-cellphone-message",
          title: "Uzun push başlığı",
          text: "Push başlığı bazı cihazlarda kesilebilir.",
        });
      }

      if (this.isPushChannel && Array.from(body).length > 180) {
        items.push({
          key: "push-body",
          type: "warn",
          icon: "mdi-cellphone-message",
          title: "Uzun push mesajı",
          text: "Push gövdesi cihaz bildirim alanında kısalabilir.",
        });
      }

      return items;
    },
    messageInsightItems() {
      if (!this.formData.body) {
        return [];
      }

      return [
        {
          key: "length-summary",
          type: "info",
          icon: "mdi-counter",
          title: "Mesaj uzunluğu",
          text: this.isSmsChannel
            ? `${this.messageLengthSummary}, ${this.smsMessageMetrics.encoding}`
            : this.messageLengthSummary,
        },
        ...this.messageWarningItems,
      ];
    },
    messageSafetyTitle() {
      return this.messageWarningItems.length
        ? `${this.messageWarningItems.length} mesaj uyarısı`
        : "Mesaj formatı uygun";
    },
    messageSafetyText() {
      if (!this.formData.body) {
        return "Mesaj içeriği bekleniyor.";
      }

      if (!this.messageWarningItems.length) {
        return "Uzunluk ve temel güvenli iletim kontrollerinde uyarı yok.";
      }

      return this.messageWarningItems
        .slice(0, 2)
        .map((item) => item.title)
        .join(" / ");
    },
    messageSimpleCheckClass() {
      return this.messageWarningItems.length
        ? "control-simple-check--warn"
        : "";
    },
    isDedupeSupported() {
      return [2, 4].includes(this.formData.notification_channel_id);
    },
    selectedRecipientType() {
      return (
        this.recipientTypeOptions.find(
          (item) => item.id === this.segment.recipient_type
        ) || this.recipientTypeOptions[0]
      );
    },
    selectedRecipientTypeText() {
      return this.selectedRecipientType?.text || "Alıcılar";
    },
    selectedRecipientTypeDescription() {
      return this.selectedRecipientType?.description || "";
    },
    availableSourcePresetOptions() {
      if (this.segment.recipient_type === "user") {
        return this.sourcePresetOptions.filter(
          (source) => source.id === "all_users"
        );
      }

      if (this.segment.recipient_type === "customer_authorized_user") {
        return this.sourcePresetOptions
          .filter((source) =>
            ["all_customers", "customers"].includes(source.id)
          )
          .map((source) => ({
            ...source,
            text:
              source.id === "all_customers"
                ? "Tüm Müşteriler"
                : "Seçili Müşteriler",
          }));
      }

      if (this.segment.recipient_type === "cluster_manager") {
        return this.sourcePresetOptions
          .filter((source) =>
            ["all_clusters", "selected_clusters"].includes(source.id)
          )
          .map((source) => ({
            ...source,
            text:
              source.id === "all_clusters"
                ? "Tüm TYA Yetkilileri"
                : "Seçilen TYA Yetkilileri",
          }));
      }

      if (this.segment.recipient_type === "management_company_user") {
        return this.sourcePresetOptions
          .filter((source) =>
            ["all_management_companies", "management_companies"].includes(
              source.id
            )
          )
          .map((source) => ({
            ...source,
            text:
              source.id === "all_management_companies"
                ? "Tüm Yönetim Şirketleri"
                : "Seçili Yönetim Şirketleri",
          }));
      }

      return this.sourcePresetOptions
        .filter((source) =>
          [
            "all_clusters",
            "selected_clusters",
            "management_companies",
            "customers",
          ].includes(source.id)
        )
        .map((source) => ({
          ...source,
          text:
            {
              all_clusters: "Tüm TYA'lar",
              selected_clusters: "Seçili TYA'lar",
              management_companies: "Seçili Yönetim Şirketleri",
              customers: "Seçili Müşteriler",
            }[source.id] || source.text,
        }));
    },
    segmentSource() {
      if (this.sourcePreset === "all_users") {
        return {
          type: "users",
          scope: "all",
          ids: [],
        };
      }

      if (this.sourcePreset === "all_customers") {
        return {
          type: "customers",
          scope: "all",
          ids: [],
        };
      }

      if (this.sourcePreset === "all_management_companies") {
        return {
          type: "management_companies",
          scope: "all",
          ids: [],
        };
      }

      if (this.sourcePreset === "customers") {
        return {
          type: "customers",
          scope: "selected",
          ids: this.search.customer_ids,
        };
      }

      if (this.sourcePreset === "management_companies") {
        return {
          type: "management_companies",
          scope: "selected",
          ids: this.search.company_ids,
        };
      }

      if (this.sourcePreset === "selected_clusters") {
        return {
          type: "clusters",
          scope: "selected",
          ids: this.search.cluster_ids,
        };
      }

      return {
        type: "clusters",
        scope: "all",
        ids: [],
      };
    },
    sourcePresetNote() {
      if (this.segment.recipient_type === "cluster_manager") {
        if (this.sourcePreset === "selected_clusters") {
          return "Seçili TYA yetkilileri listelenecek.";
        }

        return "Tüm aktif TYA yetkilileri listelenecek.";
      }

      if (this.segment.recipient_type === "management_company_user") {
        if (this.sourcePreset === "management_companies") {
          return "Seçili yönetim şirketlerindeki aktif kullanıcılar listelenecek; rol filtresiyle daraltılabilir.";
        }

        return "Tüm aktif yönetim şirketlerindeki aktif kullanıcılar listelenecek; rol filtresiyle daraltılabilir.";
      }

      if (this.sourcePreset === "all_users") {
        return "Tüm kayıtlı kullanıcı havuzu üzerinden alıcılar bulunacak.";
      }

      if (this.sourcePreset === "all_customers") {
        return "Aktif müşteri havuzu üzerinden yetkili kullanıcılar bulunacak.";
      }

      if (this.sourcePreset === "customers") {
        return "Seçili müşterilerin aktif TYA ilişkileri üzerinden alıcılar bulunacak.";
      }

      if (this.sourcePreset === "management_companies") {
        return "Seçili yönetim şirketlerinin aktif TYA ilişkileri üzerinden alıcılar bulunacak.";
      }

      if (this.sourcePreset === "selected_clusters") {
        return "Seçili TYA'lar üzerinden alıcılar bulunacak.";
      }

      return "Tüm aktif TYA havuzu üzerinden alıcılar bulunacak.";
    },
    sourceTypeForCriteria() {
      return this.segmentSource.type;
    },
    segmentSummaryText() {
      if (this.segmentPreviewMeta?.summary) {
        return this.segmentPreviewMeta.summary;
      }

      return `${this.selectedRecipientTypeText} listeleniyor.`;
    },
    selectedCriterionDefinitions() {
      return this.selectedCriteria.map((selected) => {
        const definition = this.criteriaDefinitions.find(
          (criterion) => criterion.key === selected.key
        );

        return (
          definition || {
            key: selected.key,
            label: selected.key,
            description: "Kriter tanımı yüklenemedi.",
          }
        );
      });
    },
    criteriaCategories() {
      const foundCategories = new Set(
        this.criteriaDefinitions.map((criterion) => criterion.category)
      );

      return this.criteriaCategoryOptions.filter(
        (category) => category.id === "all" || foundCategories.has(category.id)
      );
    },
    filteredCriteriaDefinitions() {
      const search = (this.criteriaSearch || "").trim().toLowerCase();

      return this.criteriaDefinitions.filter((criterion) => {
        if (
          this.activeCriteriaCategory !== "all" &&
          criterion.category !== this.activeCriteriaCategory
        ) {
          return false;
        }

        if (!search) {
          return true;
        }

        return [
          criterion.key,
          criterion.label,
          criterion.description,
          criterion.category,
        ]
          .join(" ")
          .toLowerCase()
          .includes(search);
      });
    },
    effectiveSelectedRecipientCount() {
      if (this.validationSummary) {
        return this.validationSummary.total_count;
      }

      return Math.max(
        0,
        this.selectedSegmentTotal +
          this.tempSelectedUserIds.length -
          this.excludedUserIds.length
      );
    },
    hasSnapshotSelection() {
      return this.selectedSegmentSelections.length > 0;
    },
    selectedSegmentIds() {
      return this.selectedSegmentSelections
        .map((selection) => selection.selection_id)
        .filter(Boolean);
    },
    selectedSegmentTotal() {
      return this.selectedSegmentSelections.reduce(
        (total, selection) => total + (selection.total || 0),
        0
      );
    },
    hasSelectedRecipients() {
      return this.effectiveSelectedRecipientCount > 0;
    },
    selectedRecipientCountLabel() {
      return this.validationSummary ? "Alıcı" : "Listeye ekli";
    },
    hasInvalidRecipients() {
      return (
        !!this.validationSummary && this.validationSummary.invalid_count > 0
      );
    },
    controlTotalTargetCount() {
      const total = this.selectedSegmentTotal + this.tempSelectedUserIds.length;

      return total > 0 ? total : this.effectiveSelectedRecipientCount;
    },
    controlExcludedCount() {
      if (this.excludedUserIds.length > 0) {
        return this.excludedUserIds.length;
      }

      return this.validationSummary?.invalid_count || 0;
    },
    controlSendableCount() {
      if (!this.validationSummary) {
        return this.effectiveSelectedRecipientCount;
      }

      if (this.hasInvalidRecipients) {
        return this.validationSummary.eligible_count;
      }

      return this.validationSummary.total_count;
    },
    controlSendableLabel() {
      return this.hasInvalidRecipients ? "Gönderilebilir" : "Gönderilecek";
    },
    controlFinalCountLabel() {
      if (this.isValidating) {
        return "kontrol ediliyor";
      }

      return this.hasInvalidRecipients ? "uygun alıcı" : "final alıcı";
    },
    controlDecisionClass() {
      if (this.hasInvalidRecipients) {
        return "control-final-summary--warn";
      }

      if (this.validationSummary) {
        return "control-final-summary--ok";
      }

      return "control-final-summary--pending";
    },
    controlDecisionTitle() {
      if (this.isValidating) {
        return "Kanal uygunluğu kontrol ediliyor";
      }

      if (this.hasInvalidRecipients) {
        return "Kanal kontrolü uyarısı";
      }

      if (this.validationSummary) {
        return "Gönderime hazır";
      }

      return "Kontrol bekliyor";
    },
    controlDecisionText() {
      if (this.isValidating) {
        return "Alıcı listesinin seçili kanala uygunluğu hesaplanıyor.";
      }

      if (this.hasInvalidRecipients) {
        return `${this.validationSummary.invalid_count} kullanıcı seçili kanal için uygun değil; uygun olmayanlar gönderime dahil edilmemeli.`;
      }

      if (this.validationSummary) {
        return "Final listedeki alıcıların tamamı seçili iletişim kanalı için uygun.";
      }

      return "Kanal uygunluğu kontrol edildiğinde final alıcı listesi burada netleşecek.";
    },
    channelStateClass() {
      if (this.hasInvalidRecipients) {
        return "control-state-card--warn";
      }

      if (this.validationSummary) {
        return "control-state-card--ok";
      }

      return "control-state-card--pending";
    },
    channelStateTitle() {
      if (this.hasInvalidRecipients) {
        return `${this.validationSummary.invalid_count} kullanıcı kanal için uygun değil`;
      }

      if (this.validationSummary) {
        return "Kanal uygun";
      }

      return "Kanal kontrolü bekliyor";
    },
    channelStateText() {
      if (this.hasInvalidRecipients) {
        return this.channelIssueSummary;
      }

      if (this.validationSummary) {
        return "Final listedeki alıcılar gönderilebilir.";
      }

      return "Kontrol adımında eksik iletişim bilgileri hesaplanır.";
    },
    channelIssueSummary() {
      if (!this.validationSummary) {
        return "";
      }

      const parts = [];

      if (this.validationSummary.missing_email_count) {
        parts.push(
          `E-posta yok: ${this.validationSummary.missing_email_count}`
        );
      }

      if (this.validationSummary.invalid_email_count) {
        parts.push(
          `E-posta geçersiz: ${this.validationSummary.invalid_email_count}`
        );
      }

      if (this.validationSummary.unsupported_email_characters_count) {
        parts.push(
          `E-posta karakteri desteklenmiyor: ${this.validationSummary.unsupported_email_characters_count}`
        );
      }

      if (this.validationSummary.missing_phone_count) {
        parts.push(
          `Telefon yok: ${this.validationSummary.missing_phone_count}`
        );
      }

      if (this.validationSummary.missing_mobile_token_count) {
        parts.push(
          `Mobil token yok: ${this.validationSummary.missing_mobile_token_count}`
        );
      }

      if (this.validationSummary.missing_user_count) {
        parts.push(
          `Kullanıcı bulunamadı: ${this.validationSummary.missing_user_count}`
        );
      }

      return parts.length
        ? parts.join(" / ")
        : "Uygun olmayanları çıkarabilir veya farklı kanal seçebilirsiniz.";
    },
    dedupeControlTitle() {
      if (!this.isDedupeEnabled) {
        return "Tekilleştirme kapalı";
      }

      if (!this.isDedupeSupported) {
        return "Tekilleştirme desteklenmiyor";
      }

      return "Tekilleştirme açık";
    },
    channelSimpleCheckClass() {
      return this.hasInvalidRecipients || !this.validationSummary
        ? "control-simple-check--warn"
        : "";
    },
    dedupeSimpleCheckClass() {
      return !this.isDedupeEnabled || !this.isDedupeSupported
        ? "control-simple-check--warn"
        : "";
    },
    hasRecipientPathSelection() {
      return this.includeResidents || this.includeCompanyUsers;
    },
    isSingleClusterScope() {
      return this.targetScope === "cluster";
    },
    isManagedCompanyScope() {
      return this.targetScope === "company";
    },
    isAllUsersScope() {
      return this.targetScope === "all";
    },
    isAllUsersWithoutRecipientPath() {
      return this.targetScope === "all" && !this.hasRecipientPathSelection;
    },
    targetScopeNote() {
      const map = {
        all: "Tüm kullanıcılar en geniş havuzdur. Gönderim öncesi alıcı sayısını ve kanala uygunluğu kontrol edin.",
        customer:
          "Müşteri kapsamı seçiliyken alıcılar, seçili müşterilerin current TYA ilişkileri üzerinden çözülür.",
        company:
          "Yönetim şirketi kapsamı seçiliyken alıcılar, seçili şirketin bağlı olduğu TYA ilişkileri üzerinden çözülür.",
        cluster:
          "Belirli TYA seçildiğinde müşteri seçimi ayrıca gösterilmez; TYA zaten current customer bilgisini belirler.",
      };

      return map[this.targetScope] || map.all;
    },
    selectedRecipientPathText() {
      const paths = [];

      if (this.includeResidents) {
        paths.push("sakinler");
      }

      if (this.includeCompanyUsers) {
        paths.push("yönetim şirketi çalışanları");
      }

      if (paths.length) {
        return paths.join(" ve ");
      }

      return this.targetScope === "all"
        ? "tüm kayıtlı kullanıcılar"
        : "henüz alıcı tipi seçilmedi";
    },
    selectedRecipientPathHighlight() {
      const statusPrefix = (value) => {
        if (value === "1") {
          return "aktif ";
        }

        if (value === "0") {
          return "pasif ";
        }

        return "";
      };

      const paths = [];

      if (this.includeResidents) {
        paths.push(`${statusPrefix(this.search.resident_is_active)}sakinler`);
      }

      if (this.includeCompanyUsers) {
        paths.push(
          `${statusPrefix(
            this.search.company_user_is_active
          )}yönetim şirketi çalışanları`
        );
      }

      if (paths.length) {
        return paths.join(" ve ");
      }

      return this.targetScope === "all"
        ? "tüm kayıtlı kullanıcılar"
        : "henüz alıcı tipi seçilmedi";
    },
    targetAudiencePrefix() {
      const clusterStatus =
        this.search.cluster_is_active === "1"
          ? "aktif "
          : this.search.cluster_is_active === "0"
          ? "pasif "
          : "";

      if (this.targetScope === "customer") {
        const customerCount = this.search.customer_ids.length;
        const customerText =
          customerCount > 0
            ? `seçili ${customerCount} müşterinin`
            : "seçili müşterilerin";

        return `${customerText} ${clusterStatus}TYA'larındaki `;
      }

      if (this.targetScope === "company") {
        return `seçili yönetim şirketlerinin ${clusterStatus}TYA'larındaki `;
      }

      if (this.targetScope === "cluster") {
        return "seçili TYA'lardaki ";
      }

      if (this.isAllUsersWithoutRecipientPath) {
        return "";
      }

      return "tüm kullanıcı havuzundaki ";
    },
    targetAudienceSentence() {
      return `${this.targetAudiencePrefix}${this.selectedRecipientPathText} listeleniyor.`;
    },
    targetingStatusText() {
      if (!this.hasRecipientScopeSelection) {
        return "kapsam detayı bekliyor";
      }

      if (this.total > 0) {
        return `${this.total} alıcı bulundu`;
      }

      return "hedef kitle hazır";
    },
    deliveryCheckText() {
      if (this.isValidating) {
        return "uygunluk kontrol ediliyor";
      }

      if (this.validationSummary) {
        return `${this.validationSummary.eligible_count} uygun, ${this.validationSummary.invalid_count} uygun değil`;
      }

      return `${this.effectiveSelectedRecipientCount} kullanıcı listede`;
    },
    hasRecipientScopeSelection() {
      if (
        this.sourcePreset === "all_clusters" ||
        this.sourcePreset === "all_customers" ||
        this.sourcePreset === "all_management_companies" ||
        this.sourcePreset === "all_users"
      ) {
        return true;
      }

      if (this.sourcePreset === "customers") {
        return this.search.customer_ids.length > 0;
      }

      if (this.sourcePreset === "management_companies") {
        return this.search.company_ids.length > 0;
      }

      return this.search.cluster_ids.length > 0;
    },
    recipientActionSummary() {
      if (!this.hasRecipientScopeSelection) {
        return "Alıcıları listelemek için kapsam ve alıcı tipi seçimini tamamlayın.";
      }

      if (this.isBulkAdding) {
        return `Filtredekiler ekleniyor: ${this.bulkAddFetched} / ${
          this.bulkAddTotal || "?"
        }`;
      }

      return `Tabloda seçili: ${this.tableSelectedUserIds.length}. Listeye ekli: ${this.effectiveSelectedRecipientCount}.`;
    },
    backendLogicSummary() {
      if (this.sourcePreset === "customers") {
        return "Müşteri kriteri kaynak havuzu olarak aranır, seçilen alıcı tipi aynı kaynak içinde bulunur.";
      }

      if (this.sourcePreset === "management_companies") {
        return "Yönetim şirketi kaynağı ve alıcı ilişkisi aynı segment genel filtre içinde aranır.";
      }

      if (this.sourcePreset === "selected_clusters") {
        return "Belirli TYA kaynağı üzerinden seçilen alıcı tipi aranır.";
      }

      return "Aktif TYA havuzu üzerinden seçilen alıcı tipi aranır.";
    },
    dedupeStatusText() {
      if (!this.isDedupeEnabled) {
        return "Kapalı";
      }

      if (!this.isDedupeSupported) {
        return "Desteksiz";
      }

      if (this.selectedSegmentSelections.length > 0) {
        return this.validationSummary ? "Uygulandı" : "Aktif";
      }

      if (!this.dedupeSummary) {
        return "Aktif";
      }

      return `${this.dedupeSummary.dedupedCount} tekil`;
    },
    dedupeHelpText() {
      if (!this.isDedupeEnabled) {
        return "Aynı kullanıcı birden fazla filtreden gelirse listeye ayrıca eklenebilir.";
      }

      if (!this.isDedupeSupported) {
        return "Bu kanal için kanala göre tekilleştirme uygulanmaz.";
      }

      return "Aynı kullanıcı birden fazla filtreden gelse bile tek kayıt olarak gönderilecek.";
    },
    deliveryRiskText() {
      if (!this.hasRecipientScopeSelection) {
        return "Bloke";
      }

      if (this.sourcePreset === "all_clusters") {
        return "Orta";
      }

      return "Düşük";
    },
    deliveryRiskClass() {
      if (!this.hasRecipientScopeSelection) {
        return "summary-metric__bad";
      }

      return this.sourcePreset === "all_clusters"
        ? "summary-metric__warn"
        : "summary-metric__good";
    },
    validationWarningClass() {
      if (!this.validationSummary) {
        return "checklist__item--warn";
      }

      return this.validationSummary.invalid_count > 0
        ? "checklist__item--warn"
        : "checklist__item--ok";
    },
    validationWarningIcon() {
      if (!this.validationSummary) {
        return "mdi-alert";
      }

      return this.validationSummary.invalid_count > 0
        ? "mdi-alert"
        : "mdi-check";
    },
    validationWarningTitle() {
      if (!this.validationSummary) {
        return "Uygunluk kontrolü bekliyor";
      }

      return this.validationSummary.invalid_count > 0
        ? `${this.validationSummary.invalid_count} alıcı uygun değil`
        : "Alıcılar listesi uygun";
    },
    validationWarningText() {
      if (!this.validationSummary) {
        return "Kanal ve alıcı listesi netleşince sistem otomatik kontrol eder.";
      }

      if (this.validationSummary.invalid_count > 0) {
        return "Uygun olmayanları çıkarabilir veya farklı kanal seçebilirsiniz.";
      }

      if (this.excludedUserIds.length > 0) {
        return `${this.excludedUserIds.length} kullanıcı alıcı listesi dışında bırakıldı.`;
      }

      return "Seçili kanal için eksik iletişim bilgisi görünmüyor.";
    },
    rawTempSelectedUserIds() {
      return this.tempSelectedItems.map((item) => item.id);
    },
    effectiveTempSelectedItems() {
      const excludedSet = new Set(this.excludedUserIds);
      const items = this.tempSelectedItems.filter(
        (item) => !excludedSet.has(item.id)
      );

      if (!this.isDedupeEnabled || !this.isDedupeSupported) {
        return items;
      }

      const seen = new Set();
      const deduped = [];

      for (const item of items) {
        const key = this.getDedupeKey(item);
        if (!key) {
          deduped.push(item);
          continue;
        }
        if (seen.has(key)) {
          continue;
        }
        seen.add(key);
        deduped.push(item);
      }

      return deduped;
    },
    tempSelectedUserIds() {
      return this.effectiveTempSelectedItems.map((item) => item.id);
    },
    tableSelectedUserIds() {
      return this.selectedItems
        .map((item) => this.resolveSelectedTableItem(item)?.id || null)
        .filter(Boolean);
    },
    tempListPreview() {
      return this.effectiveTempSelectedItems.slice(
        0,
        this.tempListPreviewLimit
      );
    },
    tempListOverflowCount() {
      const overflow =
        this.effectiveTempSelectedItems.length - this.tempListPreviewLimit;
      return overflow > 0 ? overflow : 0;
    },
    bulkAddProgress() {
      if (!this.bulkAddTotal) {
        return 0;
      }

      return Math.min(
        100,
        Math.round((this.bulkAddFetched / this.bulkAddTotal) * 100)
      );
    },
    tempListItems() {
      if (!this.tempListDialog) {
        return [];
      }

      let items = this.effectiveTempSelectedItems;

      const query = (this.tempListQuery || "").trim().toLowerCase();
      if (query) {
        items = items.filter((item) => {
          const name = (item.name || "").toLowerCase();
          const email = (item.email || "").toLowerCase();
          const phone = (item.phone || "").toLowerCase();
          const id = String(item.id || "");
          return (
            name.includes(query) ||
            email.includes(query) ||
            phone.includes(query) ||
            id.includes(query)
          );
        });
      }

      const emailFilter = this.normalizeEmail(this.tempListSearch.email);
      if (emailFilter) {
        items = items.filter((item) =>
          (this.normalizeEmail(item.email) || "").includes(emailFilter)
        );
      }

      const phoneFilter = this.normalizePhone(this.tempListSearch.phone);
      if (phoneFilter) {
        items = items.filter((item) =>
          (this.normalizePhone(item.phone) || "").includes(phoneFilter)
        );
      }

      return items;
    },
    dedupeSummary() {
      if (!this.isDedupeEnabled || !this.isDedupeSupported) {
        return null;
      }

      let duplicateCount = 0;
      const seen = new Set();

      for (const item of this.tempSelectedItems) {
        const key = this.getDedupeKey(item);
        if (!key) {
          continue;
        }
        if (seen.has(key)) {
          duplicateCount += 1;
        } else {
          seen.add(key);
        }
      }

      const dedupedCount = this.effectiveTempSelectedItems.length;

      return {
        duplicateCount,
        dedupedCount,
      };
    },
    duplicateGroups() {
      if (!this.isDedupeSupported) {
        return [];
      }

      const map = new Map();

      for (const item of this.tempSelectedItems) {
        const key = this.getDedupeKey(item);
        if (!key) {
          continue;
        }
        if (!map.has(key)) {
          map.set(key, []);
        }
        map.get(key).push(item);
      }

      const groups = [];
      for (const [key, items] of map.entries()) {
        if (items.length < 2) {
          continue;
        }
        groups.push({
          key,
          label: key,
          items,
        });
      }

      return groups;
    },
    duplicateSummary() {
      if (!this.isDedupeSupported) {
        return { groupCount: 0, duplicateCount: 0 };
      }

      let duplicateCount = 0;
      const groups = this.duplicateGroups;

      groups.forEach((group) => {
        duplicateCount += group.items.length - 1;
      });

      return {
        groupCount: groups.length,
        duplicateCount,
      };
    },
  },
  data() {
    return {
      rules: validation,
      isSending: false,
      isBulkAdding: false,
      isDeliveryProvidersLoading: false,
      isProviderCreditLoading: false,
      providerCredit: null,
      providerCreditRequestId: 0,
      deliveryProviderGroups: [],
      bulkAddFetched: 0,
      bulkAddTotal: 0,
      bulkAddPage: 1,
      bulkAddCancelled: false,
      bulkAddPerPage: 2000,
      currentStep: "message",
      targetScope: "all",
      sourcePreset: "all_clusters",
      segment: {
        recipient_type: "resident",
      },
      selectedCriteria: [],
      criteriaDefinitions: [],
      criteriaDrawer: false,
      criteriaSearch: "",
      activeCriteriaCategory: "all",
      isCriteriaLoading: false,
      segmentPreviewMeta: null,
      isListReloadQueued: false,
      selectedSegmentSelections: [],
      excludedUserIds: [],
      includeResidents: false,
      includeCompanyUsers: false,
      customerStatus: "active",
      sendTiming: "now",
      isDedupeEnabled: false,
      tempSelectedItems: [],
      validationSummary: null,
      isValidating: false,
      validationRequestId: 0,
      isRemovingInvalid: false,
      tempListDialog: false,
      tempListQuery: "",
      tempListPerPage: 20,
      tempListPage: 1,
      tempListPreviewLimit: 20,
      selectionPreviewItems: [],
      selectionPreviewTotal: 0,
      selectionPreviewPage: 1,
      selectionPreviewPerPage: 20,
      selectionPreviewRequestId: 0,
      isSelectionPreviewLoading: false,
      showOnlyDuplicates: false,
      showAdvancedTools: false,
      manualUserIdInput: "",
      tempListSearch: {
        email: null,
        phone: null,
      },
      activeStatusOptions: [
        { id: "1", text: "Aktif" },
        { id: "0", text: "Pasif" },
      ],
      customerStatusOptions: [
        { id: "active", text: "Aktif" },
        { id: "all", text: "Tümü" },
        { id: "passive", text: "Pasif" },
      ],
      recipientTypeOptions: [
        {
          id: "resident",
          text: "Sakin",
          description:
            "Sakinler seçildiğinde kaynak TYA'lardan sakin ilişkileri bulunur.",
        },
        {
          id: "cluster_manager",
          text: "TYA Yetkilisi",
          description:
            "Seçilen kaynak TYA'ların yetkili kullanıcıları bulunur.",
        },
        {
          id: "management_company_user",
          text: "Yönetim Şirketi",
          description: "Seçilen kaynak Yönetim şirketi çalışanları bulunur.",
        },
        {
          id: "customer_authorized_user",
          text: "Müşteri",
          description: "Müşteri Yetkili ilişkisi üzerinden bulunur.",
        },
        {
          id: "user",
          text: "Tüm Kullanıcılar",
          description: "Tüm kullanıcı havuzundan bulunur.",
        },
      ],
      sourcePresetOptions: [
        { id: "all_clusters", text: "Tüm TYA'lar" },
        { id: "all_customers", text: "Tüm Müşteriler" },
        { id: "all_management_companies", text: "Tüm Yönetim Şirketleri" },
        { id: "all_users", text: "Tüm Kullanıcılar" },
        { id: "customers", text: "Seçili Müşteriler" },
        { id: "management_companies", text: "Seçili Yönetim Şirketleri" },
        { id: "selected_clusters", text: "Seçili TYA'lar" },
      ],
      criteriaCategoryOptions: [
        { id: "all", text: "Tümü" },
        { id: "recommended", text: "Önerilen" },
        { id: "cluster", text: "TYA" },
        { id: "resident", text: "Sakin" },
        { id: "contact", text: "İletişim" },
        { id: "mobile", text: "Mobil" },
        { id: "finance", text: "Finans" },
        { id: "payment", text: "Ödeme" },
        { id: "integration", text: "Entegrasyon" },
        { id: "customer", text: "Müşteri" },
        { id: "management_company", text: "Yönetim Şirketi" },
      ],
      sendTimingOptions: [
        { id: "now", text: "Hemen gönder" },
        { id: "scheduled", text: "Zamanla" },
      ],
      formData: {
        notification_channel_id: null,
        delivery_provider: null,
        subject: "",
        body: "",
      },
      titleBarAttrs: {},
      options: {
        page: parseInt(this.$route.query.page, 10) || 1,
        itemsPerPage:
          parseInt(this.$route.query.per_page, 10) ||
          parseInt(window.localStorage.getItem("perPage"), 10) ||
          20,
        sortBy: ["name"],
        sortDesc: [false],
      },
      search: {
        query: null,
        name: null,
        email: null,
        phone: null,
        role_ids: [],
        recipient_scope: "all_users",
        customer_ids: [],
        company_ids: [],
        company_id: null,
        cluster_ids: [],
        resident_count: {},
        company_user_count: {},
        user_is_active: null,
        resident_is_active: null,
        company_user_is_active: null,
        cluster_is_active: null,
        management_company_is_active: null,
      },
      headers: [
        {
          text: "Adı",
          value: "name",
          searchable: "text",
          width: "420px",
        },
        {
          text: "Rolleri",
          value: "roles",
          width: "220px",
        },
        {
          text: "Telefon",
          value: "phone",
          searchable: "text",
        },
        {
          text: "E-posta",
          value: "email",
          searchable: "text",
        },
      ],
      tempListHeaders: [
        { text: "Adı", value: "name", width: "420px" },
        { text: "E-posta", value: "email", searchable: "text" },
        { text: "Telefon", value: "phone", searchable: "text" },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
    };
  },
  watch: {
    search: {
      handler: debounce(function () {
        this.options = {
          ...this.options,
          page: 1,
        };
        this.selectedItems = [];
        this.clearSegmentSelection();
        this.updateSegmentPreviewUrlParams();
        this.loadList();
      }, 250),
      deep: true,
    },
    selectedCriteria: {
      handler: debounce(function () {
        this.handleSegmentChanged();
      }, 250),
      deep: true,
    },
    "segment.recipient_type"() {
      this.loadCriteriaList();
    },
    criteriaDrawer(val) {
      if (val) {
        this.loadCriteriaList();
      }
    },
    tempListDialog(val) {
      if (val && this.hasSnapshotSelection) {
        this.loadSelectionPreview();
      }
    },
    tempListQuery: debounce(function () {
      if (this.tempListDialog && this.hasSnapshotSelection) {
        this.selectionPreviewPage = 1;
        this.loadSelectionPreview();
      }
    }, 250),
    includeResidents() {
      this.syncRecipientPathFilters();
    },
    includeCompanyUsers() {
      this.syncRecipientPathFilters();
    },
    "formData.notification_channel_id"(val) {
      this.excludedUserIds = [];
      this.setDefaultDeliveryProvider(val);
      this.loadProviderCredit();

      if (val) {
        this.scheduleValidation();
      } else {
        this.validationSummary = null;
      }
    },
    "formData.delivery_provider"() {
      this.excludedUserIds = [];
      this.loadProviderCredit();
      this.scheduleValidation();
    },
    isDedupeEnabled() {
      this.scheduleValidation();
    },
    showOnlyDuplicates() {
      this.tempListQuery = "";
      this.tempListSearch = {
        email: null,
        phone: null,
      };
    },
  },
  mounted() {
    this.syncRecipientPathFilters();
    this.loadDeliveryProviders();
    this.loadCriteriaList();
    this.loadList();
  },
  methods: {
    saveDraft() {
      const draft = {
        formData: this.formData,
        search: this.search,
        segment: this.segment,
        sourcePreset: this.sourcePreset,
        selectedCriteria: this.selectedCriteria,
        customerStatus: this.customerStatus,
        sendTiming: this.sendTiming,
        isDedupeEnabled: this.isDedupeEnabled,
        tempSelectedItems: this.tempSelectedItems,
      };

      localStorage.setItem("admin-notification-draft", JSON.stringify(draft));
      this.$toast.success("Taslak kaydedildi.");
    },
    clearDraft() {
      localStorage.removeItem("admin-notification-draft");
      this.formData = {
        notification_channel_id: null,
        delivery_provider: null,
        subject: "",
        body: "",
      };
      this.providerCredit = null;
      this.isProviderCreditLoading = false;
      this.segment = {
        recipient_type: "resident",
      };
      this.sourcePreset = "all_clusters";
      this.selectedCriteria = [];
      this.sendTiming = "now";
      this.isDedupeEnabled = false;
      this.clearSelectedUsers();
      this.resetFilters();
      this.currentStep = "message";
      this.$toast.success("Ekran temizlendi.");
    },
    selectTemplate() {
      this.$toast.info("Şablon seçimi daha sonra bağlanacak.");
    },
    loadDeliveryProviders() {
      this.isDeliveryProvidersLoading = true;

      this.$api
        .query("api-super-admin/notification-delivery-providers")
        .then((response) => {
          this.deliveryProviderGroups = response.data?.data || [];
          this.setDefaultDeliveryProvider(
            this.formData.notification_channel_id
          );
          this.loadProviderCredit();
        })
        .catch((error) => {
          this.deliveryProviderGroups = [];
          this.handleError(error);
        })
        .finally(() => {
          this.isDeliveryProvidersLoading = false;
        });
    },
    setDefaultDeliveryProvider(channelId) {
      if (!channelId) {
        this.formData.delivery_provider = null;
        return;
      }

      const group = this.deliveryProviderGroups.find(
        (item) => item.channel_id === channelId
      );

      if (!group) {
        this.formData.delivery_provider = null;
        return;
      }

      const providerIds = (group.providers || []).map(
        (provider) => provider.id
      );
      if (!providerIds.includes(this.formData.delivery_provider)) {
        this.formData.delivery_provider =
          group.default_provider || providerIds[0] || null;
      }
    },
    loadProviderCredit() {
      const requestId = ++this.providerCreditRequestId;

      if (!this.shouldShowProviderCredit) {
        this.providerCredit = null;
        this.isProviderCreditLoading = false;
        return;
      }

      this.isProviderCreditLoading = true;

      this.$api
        .query("api-super-admin/notification-delivery-provider-credit", {
          params: {
            notification_channel_id: this.formData.notification_channel_id,
            delivery_provider: this.formData.delivery_provider,
          },
        })
        .then((response) => {
          if (requestId !== this.providerCreditRequestId) {
            return;
          }

          this.providerCredit = response.data?.data || null;
        })
        .catch(() => {
          if (requestId !== this.providerCreditRequestId) {
            return;
          }

          this.providerCredit = {
            available: false,
            message: "Kredi bilgisi alınamadı.",
          };
        })
        .finally(() => {
          if (requestId === this.providerCreditRequestId) {
            this.isProviderCreditLoading = false;
          }
        });
    },
    formatNumber(value) {
      return new Intl.NumberFormat("tr-TR").format(Number(value) || 0);
    },
    isGsm7Text(value) {
      const basic =
        "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
      const extended = "^{}\\[~]|€";

      return Array.from(value || "").every(
        (char) => basic.includes(char) || extended.includes(char)
      );
    },
    countGsm7Characters(value) {
      const extended = "^{}\\[~]|€";

      return Array.from(value || "").reduce(
        (total, char) => total + (extended.includes(char) ? 2 : 1),
        0
      );
    },
    containsUrl(value) {
      return /(https?:\/\/|www\.|[a-z0-9-]+\.[a-z]{2,})/i.test(value || "");
    },
    hasAggressivePunctuation(value) {
      return /[!?]{3,}/.test(value || "");
    },
    isMostlyUppercase(value) {
      const letters = Array.from(value || "").filter((char) =>
        /[A-Za-zÇĞİÖŞÜçğıöşü]/.test(char)
      );

      if (letters.length < 16) {
        return false;
      }

      const uppercase = letters.filter(
        (char) => char === char.toLocaleUpperCase("tr-TR")
      );

      return uppercase.length / letters.length > 0.72;
    },
    canEnterStep(step) {
      if (step === "message") {
        return true;
      }

      if (step === "target") {
        return this.isMessageReady;
      }

      if (step === "control") {
        return (
          this.isMessageReady &&
          this.hasRecipientScopeSelection &&
          this.hasSelectedRecipients
        );
      }

      return false;
    },
    isStepCompleted(step) {
      if (step === "message") {
        return this.isMessageReady;
      }

      if (step === "target") {
        return this.hasRecipientScopeSelection && this.hasSelectedRecipients;
      }

      if (step === "control") {
        return !!this.validationSummary && this.hasSelectedRecipients;
      }

      return false;
    },
    stepCardClass(step) {
      return {
        "step-card--active": this.currentStep === step,
        "step-card--completed":
          this.currentStep !== step && this.isStepCompleted(step),
        "step-card--locked": !this.canEnterStep(step),
      };
    },
    goToStep(step) {
      if (!this.canEnterStep(step)) {
        if (step === "target") {
          this.$toast.warning("Hedef kitleye geçmeden önce mesajı tamamlayın.");
        } else if (step === "control") {
          this.$toast.warning(
            "Kontrole geçmeden önce hedef kitleyi tamamlayıp alıcı ekleyin."
          );
        }
        return;
      }

      this.currentStep = step;

      if (step === "control") {
        this.scheduleValidation();
      }
    },
    handleSegmentChanged() {
      this.options.page = 1;
      this.selectedItems = [];
      this.clearSegmentSelection();
      this.updateSegmentPreviewUrlParams();
      this.loadList();
    },
    handleRecipientTypeChanged() {
      this.selectedCriteria = [];
      this.normalizeSourcePresetForRecipient();
      this.loadCriteriaList();
      this.handleSegmentChanged();
    },
    normalizeSourcePresetForRecipient() {
      const availableSourceIds = this.availableSourcePresetOptions.map(
        (source) => source.id
      );

      if (!availableSourceIds.includes(this.sourcePreset)) {
        this.sourcePreset = availableSourceIds[0] || "all_clusters";
      }
    },
    handleSourcePresetChanged() {
      this.selectedCriteria = [];
      this.search.customer_ids = [];
      this.search.company_ids = [];
      this.search.company_id = null;
      this.search.cluster_ids = [];
      this.loadCriteriaList();
      this.handleSegmentChanged();
    },
    handleTableOptionsUpdated(nextOptions) {
      const nextPerPage =
        Number(nextOptions.itemsPerPage) || this.itemsPerPage || 20;
      const nextPage = Number(nextOptions.page) || 1;

      const shouldReload =
        nextPage !== Number(this.options.page || 1) ||
        nextPerPage !== Number(this.options.itemsPerPage || this.itemsPerPage);

      this.options = {
        ...this.options,
        ...nextOptions,
        page: nextPage,
        itemsPerPage: nextPerPage,
      };
      this.itemsPerPage = nextPerPage;
      window.localStorage.setItem("perPage", nextPerPage);
      this.updateSegmentPreviewUrlParams(nextPage, nextPerPage);

      if (shouldReload) {
        this.selectedItems = [];
        this.loadList();
      }
    },
    updateSegmentPreviewUrlParams(
      page = this.options.page,
      perPage = this.options.itemsPerPage || this.itemsPerPage
    ) {
      const params = new URLSearchParams(window.location.search);
      params.set("page", String(Number(page) || 1));
      params.set("per_page", String(Number(perPage) || 20));

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({ path: newUrl }, "", newUrl);
    },
    buildSegmentPayload(extra = {}) {
      const payload = {
        recipient_type: this.segment.recipient_type,
        source: this.segmentSource,
        criteria: this.selectedCriteria.map((criterion) => ({
          key: criterion.key,
          operator: criterion.operator || "equals",
          value:
            criterion.value === undefined || criterion.value === null
              ? true
              : criterion.value,
        })),
        common_filters: {
          query: this.search.query || null,
          name: this.search.name || null,
          email: this.search.email || null,
          phone: this.search.phone || null,
          role_ids: this.search.role_ids || [],
        },
        options: {
          include_passive_records: false,
        },
      };

      if (extra.pagination) {
        payload.pagination = extra.pagination;
      }

      return payload;
    },
    clearSegmentSelection() {
      this.validationSummary = null;
    },
    buildRecipientSelectionPayload(includeIds = false) {
      const payload = {
        notification_channel_id: this.formData.notification_channel_id,
      };

      if (this.formData.delivery_provider) {
        payload.delivery_provider = this.formData.delivery_provider;
      }

      if (this.selectedSegmentIds.length > 0) {
        payload.selection_ids = this.selectedSegmentIds;
      }

      if (this.tempSelectedUserIds.length > 0) {
        payload.user_ids = this.tempSelectedUserIds;
      }

      if (this.excludedUserIds.length > 0) {
        payload.excluded_user_ids = this.excludedUserIds;
      }

      if (includeIds) {
        payload.include_ids = true;
      }

      return payload;
    },
    roleLabels(value) {
      if (!value) {
        return [];
      }

      const labels = Array.isArray(value)
        ? value
        : String(value)
            .split(",")
            .map((role) => role.trim());

      return Array.from(new Set(labels.filter(Boolean)));
    },
    formatRoleSummary(value) {
      const roles = this.roleLabels(value);

      if (!roles.length) {
        return "-";
      }

      const overflow = roles.length - 1;
      return overflow > 0 ? `${roles[0]} +${overflow} rol` : roles[0];
    },
    primaryRelationGroup(item) {
      return this.relationGroups(item)[0] || null;
    },
    relationGroups(item) {
      const context = item?.relation_context || {};

      return Object.entries(context)
        .map(([key, group]) => ({
          key,
          label: group?.label || key,
          total: Number(group?.total || 0),
          items: Array.isArray(group?.items) ? group.items : [],
          overflow: Number(group?.overflow || 0),
        }))
        .filter((group) => group.total > 0);
    },
    formatRelationGroup(group) {
      const names = group.items
        .map((item) => item?.name)
        .filter(Boolean)
        .join(", ");

      if (!names) {
        return `${group.total} kayıt`;
      }

      return group.overflow > 0 ? `${names} +${group.overflow}` : names;
    },
    openCriteriaDrawer() {
      this.criteriaDrawer = true;
      this.loadCriteriaList();
    },
    loadCriteriaList() {
      if (this.isCriteriaLoading) {
        return;
      }

      this.isCriteriaLoading = true;

      this.$api
        .query("api-super-admin/notification-segment-criteria", {
          params: {
            recipient_type: this.segment.recipient_type,
            source_type: this.sourceTypeForCriteria,
          },
        })
        .then((response) => {
          this.criteriaDefinitions = response.data?.data || [];
          const allowedKeys = new Set(
            this.criteriaDefinitions.map((criterion) => criterion.key)
          );
          this.selectedCriteria = this.selectedCriteria.filter((criterion) =>
            allowedKeys.has(criterion.key)
          );

          if (
            this.activeCriteriaCategory !== "all" &&
            !this.criteriaCategories.some(
              (category) => category.id === this.activeCriteriaCategory
            )
          ) {
            this.activeCriteriaCategory = "all";
          }
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isCriteriaLoading = false;
        });
    },
    isCriterionSelected(key) {
      return this.selectedCriteria.some((criterion) => criterion.key === key);
    },
    addCriterion(criterion) {
      if (this.isCriterionSelected(criterion.key)) {
        return;
      }

      this.selectedCriteria.push({
        key: criterion.key,
        operator: criterion.default_operator || "equals",
        value:
          criterion.default_value === undefined
            ? true
            : criterion.default_value,
      });
    },
    removeCriterion(key) {
      this.selectedCriteria = this.selectedCriteria.filter(
        (criterion) => criterion.key !== key
      );
    },
    setTargetScope(scope) {
      this.targetScope = scope;

      if (scope === "company") {
        this.search.recipient_scope = "managed_company_clusters";
        this.search.customer_ids = [];
        this.search.cluster_ids = [];
        return;
      }

      if (scope === "cluster") {
        this.search.recipient_scope = "single_cluster";
        this.search.customer_ids = [];
        return;
      }

      this.search.recipient_scope = "all_users";
      this.search.cluster_ids = [];

      if (scope === "all") {
        this.search.customer_ids = [];
        this.search.company_ids = [];
        this.search.company_id = null;
      }
    },
    syncRecipientPathFilters() {
      if (this.includeResidents && !this.includeCompanyUsers) {
        this.search.resident_count = { min: 1 };
        this.search.company_user_count = {};
        return;
      }

      if (!this.includeResidents && this.includeCompanyUsers) {
        this.search.resident_count = {};
        this.search.company_user_count = { min: 1 };
        return;
      }

      this.search.resident_count = {};
      this.search.company_user_count = {};
    },
    recipientPathText(type) {
      if (this.targetScope === "customer") {
        return type === "resident"
          ? "customer -> cluster -> resident -> user"
          : "customer -> cluster_company -> company -> company_user -> user";
      }

      if (this.targetScope === "company") {
        return type === "resident"
          ? "company -> cluster -> resident -> user"
          : "company -> company_user -> user";
      }

      if (this.targetScope === "cluster") {
        return type === "resident"
          ? "cluster -> resident -> user"
          : "cluster -> cluster_company -> company_user -> user";
      }

      return type === "resident" ? "resident -> user" : "company_user -> user";
    },
    normalizeEmail(value) {
      if (!value) {
        return null;
      }
      return value.trim().toLowerCase();
    },
    normalizePhone(value) {
      if (!value) {
        return null;
      }
      return value.trim().replace(/\s+/g, "");
    },
    getDedupeKey(item) {
      const channel = this.formData.notification_channel_id;
      if (channel === 4) {
        return this.normalizeEmail(item.email);
      }
      if (channel === 2) {
        return this.normalizePhone(item.phone);
      }
      return null;
    },
    parseManualUserIds(input) {
      const tokens = input
        .split(/[\s,;]+/)
        .map((token) => token.trim())
        .filter(Boolean);

      const ids = [];
      const invalid = [];

      tokens.forEach((token) => {
        if (/^\d+$/.test(token)) {
          const value = Number(token);
          if (value > 0) {
            ids.push(value);
            return;
          }
        }
        invalid.push(token);
      });

      return { ids, invalid };
    },
    parseManualUserInput(input) {
      const trimmed = (input || "").trim();
      if (!trimmed) {
        return { items: [], ids: [], invalid: [], parsedJson: false };
      }

      if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          let items = [];

          if (Array.isArray(parsed)) {
            items = parsed;
          } else if (parsed && Array.isArray(parsed.data)) {
            items = parsed.data;
          } else if (parsed && Array.isArray(parsed.items)) {
            items = parsed.items;
          } else if (parsed && parsed.id) {
            items = [parsed];
          }

          if (items.length) {
            return { items, ids: [], invalid: [], parsedJson: true };
          }
        } catch (error) {
          // fall back to ID parsing
        }
      }

      const { ids, invalid } = this.parseManualUserIds(trimmed);
      return { items: [], ids, invalid, parsedJson: false };
    },
    normalizeManualUserInput(item) {
      const source = item && item.user ? item.user : item;
      const id = source?.id ?? source?.user_id ?? item?.user_id;

      if (!id) {
        return null;
      }

      return {
        id,
        name:
          source?.name ??
          source?.full_name ??
          source?.display_name ??
          source?.user_name ??
          null,
        email: source?.email ?? source?.mail ?? null,
        phone: source?.phone ?? source?.mobile ?? null,
      };
    },
    resolveSelectedTableItem(item) {
      if (item && typeof item === "object" && item.id) {
        return item;
      }

      return this.list.find((listItem) => listItem.id === item) || null;
    },
    addManualUserIds() {
      const input = this.manualUserIdInput || "";
      const { items, ids, invalid, parsedJson } =
        this.parseManualUserInput(input);

      if (!items.length && !ids.length) {
        this.$toast.error("Geçerli bir kullanıcı ID'si veya JSON bulunamadı.");
        return;
      }

      const existingIds = new Set(this.rawTempSelectedUserIds);
      const initialCount = this.tempSelectedItems.length;
      this.clearSegmentSelection();

      let invalidJsonCount = 0;

      if (items.length) {
        items.forEach((item) => {
          const normalized = this.normalizeManualUserInput(item);
          if (!normalized) {
            invalidJsonCount += 1;
            return;
          }
          if (!existingIds.has(normalized.id)) {
            this.tempSelectedItems.push(normalized);
            existingIds.add(normalized.id);
          }
        });
      } else if (ids.length) {
        const uniqueIds = Array.from(new Set(ids));
        uniqueIds.forEach((id) => {
          if (!existingIds.has(id)) {
            this.tempSelectedItems.push({
              id,
              name: null,
              email: null,
              phone: null,
            });
            existingIds.add(id);
          }
        });
      }

      const addedCount = this.tempSelectedItems.length - initialCount;

      if (addedCount > 0) {
        this.$toast.success(`${addedCount} kullanıcı listeye eklendi.`);
      } else {
        this.$toast.warning("Yeni kullanıcı eklenmedi (hepsi zaten listede).");
      }

      if (invalidJsonCount > 0) {
        this.$toast.warning(
          `${invalidJsonCount} kayıt hatalı olduğu için atlandı.`
        );
      }

      if (invalid.length) {
        this.$toast.warning(
          `${invalid.length} geçersiz ID atlandı: ${invalid
            .slice(0, 5)
            .join(", ")}${invalid.length > 5 ? "..." : ""}`
        );
      }

      if (!parsedJson && ids.length) {
        this.$toast.warning(
          "Detaylar bulunmadığı için tekilleştirme/uygunluk sınırlı çalışabilir."
        );
      }

      this.manualUserIdInput = "";
      this.scheduleValidation();
    },
    keepOnlyUserInGroup(groupKey, keepId) {
      const remaining = [];

      for (const item of this.tempSelectedItems) {
        const key = this.getDedupeKey(item);
        if (key !== groupKey) {
          remaining.push(item);
          continue;
        }
        if (item.id === keepId) {
          remaining.push(item);
        }
      }

      this.tempSelectedItems = remaining;
      this.scheduleValidation();
    },
    normalizeSelectedIds(payload) {
      const selected = Array.isArray(payload?.id) ? payload.id : payload;
      const values = Array.isArray(selected)
        ? selected
        : [selected && typeof selected === "object" ? selected.id : selected];

      return values
        .filter((id) => id !== null && id !== undefined && id !== "")
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0);
    },
    handleCompanySelected(payload) {
      if (
        this.sourcePreset === "management_companies" ||
        this.sourcePreset === "selected_clusters"
      ) {
        const previousCompanyIds = this.search.company_ids.join(",");
        const nextCompanyIds = this.normalizeSelectedIds(payload);

        this.search.company_ids = nextCompanyIds;
        this.search.company_id = nextCompanyIds[0] || null;

        if (
          this.sourcePreset === "selected_clusters" &&
          previousCompanyIds !== nextCompanyIds.join(",")
        ) {
          this.search.cluster_ids = [];
        }

        return;
      }

      const nextCompanyId = this.normalizeSelectedIds(payload)[0] || null;
      if (this.search.company_id !== nextCompanyId) {
        this.search.cluster_ids = [];
      }
      this.search.company_id = nextCompanyId;
    },
    handleClusterSelected(payload) {
      this.search.cluster_ids = this.normalizeSelectedIds(payload);
    },
    resetFilters() {
      this.targetScope = "all";
      this.sourcePreset = "all_clusters";
      this.segment = {
        recipient_type: "resident",
      };
      this.selectedCriteria = [];
      this.includeResidents = false;
      this.includeCompanyUsers = false;
      this.customerStatus = "active";
      this.search = {
        query: null,
        name: null,
        email: null,
        phone: null,
        role_ids: [],
        recipient_scope: "all_users",
        customer_ids: [],
        company_ids: [],
        company_id: null,
        cluster_ids: [],
        resident_count: {},
        company_user_count: {},
        user_is_active: null,
        resident_is_active: null,
        company_user_is_active: null,
        cluster_is_active: null,
        management_company_is_active: null,
      };
      this.syncRecipientPathFilters();
      this.clearSelectedUsers();
      this.loadCriteriaList();
    },
    getParams(customOptions = this.options) {
      if (!this.hasRecipientScopeSelection) {
        return null;
      }

      const params = this.buildSearchParams(this.search, customOptions);
      delete params.company_id;

      if (!this.includeResidents) {
        delete params.resident_is_active;
      }

      if (!this.includeCompanyUsers) {
        delete params.company_user_is_active;
      }

      if (this.targetScope === "company") {
        delete params.cluster_id;
        delete params.customer_ids;
        params.managed_by_company_id = this.search.company_id;
      } else if (this.targetScope === "cluster") {
        delete params.customer_ids;
        delete params.managed_by_company_id;
      } else if (this.targetScope === "customer") {
        delete params.cluster_id;
        delete params.managed_by_company_id;
      } else {
        delete params.cluster_id;
        delete params.customer_ids;
        delete params.managed_by_company_id;
      }

      return params;
    },
    resetRecipientList() {
      this.list = [];
      this.total = 0;
      this.selectedItems = [];
      this.footTotals = [];
      this.segmentPreviewMeta = null;
    },
    loadList() {
      if (this.isLoading) {
        this.isListReloadQueued = true;
        return;
      }

      if (!this.hasRecipientScopeSelection) {
        this.resetRecipientList();
        return;
      }

      this.isLoading = true;
      this.isListReloadQueued = false;
      this.list = [];
      this.footTotals = [];
      const perPage =
        Number(this.options.itemsPerPage) || Number(this.itemsPerPage) || 20;

      const payload = this.buildSegmentPayload({
        pagination: {
          page: this.options.page || 1,
          per_page: perPage,
        },
      });

      this.$api
        .post("api-super-admin/notification-segment-preview", payload)
        .then((response) => {
          this.list = response.data?.data || [];
          this.segmentPreviewMeta = response.data?.meta || null;
          this.total = this.segmentPreviewMeta?.total || 0;
          this.itemsPerPage = this.segmentPreviewMeta?.per_page || perPage;
          this.options = {
            ...this.options,
            page: this.segmentPreviewMeta?.page || 1,
            itemsPerPage: this.itemsPerPage,
          };
          this.updateSegmentPreviewUrlParams();
          this.footTotals = this.segmentPreviewMeta?.totals || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;

          if (this.isListReloadQueued) {
            this.isListReloadQueued = false;
            this.loadList();
          }
        });
    },
    handleSendClick() {
      if (this.isSending) {
        return;
      }

      if (!this.formData.notification_channel_id) {
        this.$toast.error("Lütfen gönderim kanalı seçin");
        return;
      }

      if (!this.validateMessageForm()) {
        this.$toast.error("Lutfen gerekli alanlari doldurun");
        this.goToStep("message");
        return;
      }

      if (!this.hasSelectedRecipients) {
        this.$toast.error("En az bir kullanici secmelisiniz");
        return;
      }

      this.$refs.confirmSend.show(
        `${this.effectiveSelectedRecipientCount} kullaniciya bildirim gonderilecek. Emin misiniz?`
      );
    },
    validateMessageForm() {
      const formRef = Array.isArray(this.$refs.sendForm)
        ? this.$refs.sendForm[0]
        : this.$refs.sendForm;

      if (formRef && typeof formRef.validate === "function") {
        return formRef.validate();
      }

      const subject = this.formData.subject || "";
      const body = this.formData.body || "";

      return (
        !!this.formData.notification_channel_id &&
        !!body &&
        subject.length <= 120 &&
        body.length <= 1000
      );
    },
    handleSendConfirmed() {
      if (this.isSending) {
        return;
      }

      const payload = {
        ...this.buildRecipientSelectionPayload(),
        notification_channel_id: this.formData.notification_channel_id,
        body: this.formData.body,
        subject: this.formData.subject || null,
      };

      this.isSending = true;

      this.$api
        .post("api-super-admin/notifications/send", payload)
        .then((response) => {
          const targetCount =
            response.data.data?.target_count ??
            this.effectiveSelectedRecipientCount;
          const batchId = response.data.data?.batch_id;
          const batchText = batchId ? ` (Batch #${batchId})` : "";
          this.$toast.success(
            `${targetCount} kullanici icin batch olusturuldu${batchText}. Gonderim arka planda basladi.`
          );
          this.$refs.confirmSend.hide();
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isSending = false;
        });
    },
    normalizeTempUser(user) {
      return {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        relation_context: user.relation_context || null,
        relation_summary: user.relation_summary || null,
      };
    },
    async addAllFilteredUsers() {
      if (this.isBulkAdding) {
        return;
      }

      if (!this.hasRecipientScopeSelection) {
        this.$toast.warning("Toplu ekleme için hedef kapsamı tamamlayın.");
        return;
      }

      this.isBulkAdding = true;
      this.bulkAddCancelled = false;
      this.bulkAddFetched = 0;
      this.bulkAddTotal = 0;
      this.bulkAddPage = 1;

      try {
        const response = await this.$api.post(
          "api-super-admin/notification-segment-resolve",
          this.buildSegmentPayload()
        );

        const result = response.data?.data || {};
        const selectionTotal = result.total || 0;
        this.selectedItems = [];
        this.bulkAddTotal = selectionTotal;
        this.bulkAddFetched = selectionTotal;

        if (!selectionTotal) {
          this.$toast.warning("Filtreye uyan kullanici bulunamadi.");
        } else {
          this.selectedSegmentSelections.push({
            selection_id: result.selection_id,
            total: selectionTotal,
            summary: this.segmentSummaryText,
          });

          this.$toast.success(
            `${selectionTotal} kullanıcı alıcı adayı olarak listeye eklendi.`
          );
        }
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isBulkAdding = false;
        this.scheduleValidation();
      }
    },
    validateRecipients: debounce(async function () {
      if (
        this.isBulkAdding ||
        !this.formData.notification_channel_id ||
        !this.hasSelectedRecipients
      ) {
        this.validationSummary = null;
        return;
      }

      const requestId = ++this.validationRequestId;
      this.isValidating = true;

      try {
        const payload = this.buildRecipientSelectionPayload();

        const response = await this.$api.post(
          "api-super-admin/notifications/validate",
          payload
        );

        if (requestId !== this.validationRequestId) {
          return;
        }

        this.validationSummary = response.data.data || null;
      } catch (error) {
        this.validationSummary = null;
        this.handleError(error);
      } finally {
        if (requestId === this.validationRequestId) {
          this.isValidating = false;
        }
      }
    }, 500),
    scheduleValidation() {
      if (this.validateRecipients) {
        this.validateRecipients();
      }
    },
    async removeInvalidRecipients() {
      if (
        this.isRemovingInvalid ||
        !this.formData.notification_channel_id ||
        !this.hasSelectedRecipients
      ) {
        return;
      }

      this.isRemovingInvalid = true;

      try {
        const payload = this.buildRecipientSelectionPayload(true);

        const response = await this.$api.post(
          "api-super-admin/notifications/validate",
          payload
        );

        const invalidIds = response.data.data?.invalid_user_ids || [];

        if (!invalidIds.length) {
          this.$toast.success("Uygun olmayan kullanici bulunamadi.");
          this.validationSummary = response.data.data || null;
          return;
        }

        const invalidSet = new Set(invalidIds);
        const excludedSet = new Set(this.excludedUserIds);
        invalidIds.forEach((id) => excludedSet.add(id));
        this.excludedUserIds = Array.from(excludedSet);

        this.tempSelectedItems = this.tempSelectedItems.filter(
          (item) => !invalidSet.has(item.id)
        );

        this.$toast.success(
          `${invalidIds.length} uygun olmayan kullanici listeden cikarildi.`
        );
        this.validationSummary = response.data.data || null;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isRemovingInvalid = false;
        this.scheduleValidation();
      }
    },
    cancelBulkAdd() {
      this.bulkAddCancelled = true;
    },
    openTempListDialog() {
      this.tempListDialog = true;

      if (this.hasSnapshotSelection) {
        this.selectionPreviewPage = 1;
        this.loadSelectionPreview();
      }
    },
    handleSelectionPreviewPageUpdated(page) {
      this.selectionPreviewPage = page;
      this.loadSelectionPreview();
    },
    handleSelectionPreviewItemsPerPageUpdated(perPage) {
      this.selectionPreviewPerPage = perPage;
      this.selectionPreviewPage = 1;
      this.loadSelectionPreview();
    },
    async loadSelectionPreview() {
      if (!this.hasSelectedRecipients) {
        this.selectionPreviewItems = [];
        this.selectionPreviewTotal = 0;
        return;
      }

      const requestId = ++this.selectionPreviewRequestId;
      this.isSelectionPreviewLoading = true;

      try {
        const payload = {
          ...this.buildRecipientSelectionPayload(),
          query: this.tempListQuery || null,
          pagination: {
            page: this.selectionPreviewPage,
            per_page: this.selectionPreviewPerPage,
          },
        };

        const response = await this.$api.post(
          "api-super-admin/notification-recipient-selection-preview",
          payload
        );

        if (requestId !== this.selectionPreviewRequestId) {
          return;
        }

        this.selectionPreviewItems = response.data?.data || [];
        this.selectionPreviewTotal = response.data?.meta?.total || 0;
        this.selectionPreviewPage = response.data?.meta?.page || 1;
        this.selectionPreviewPerPage = response.data?.meta?.per_page || 20;
      } catch (error) {
        this.handleError(error);
      } finally {
        if (requestId === this.selectionPreviewRequestId) {
          this.isSelectionPreviewLoading = false;
        }
      }
    },
    clearSelectedUsers() {
      this.tempSelectedItems = [];
      this.tempListQuery = "";
      this.tempListPage = 1;
      this.selectionPreviewItems = [];
      this.selectionPreviewTotal = 0;
      this.selectionPreviewPage = 1;
      this.tempListSearch = {
        email: null,
        phone: null,
      };
      this.selectedSegmentSelections = [];
      this.excludedUserIds = [];
      this.clearSegmentSelection();
    },
    removeSelectedUser(userId) {
      const normalizedUserId = Number(userId);

      if (!normalizedUserId) {
        return;
      }

      this.tempSelectedItems = this.tempSelectedItems.filter(
        (item) => item.id !== normalizedUserId
      );

      if (this.hasSnapshotSelection) {
        const excludedSet = new Set(this.excludedUserIds);
        excludedSet.add(normalizedUserId);
        this.excludedUserIds = Array.from(excludedSet);
        this.loadSelectionPreview();
      }

      this.scheduleValidation();
    },
    addSelectedUsersToTemp() {
      if (!this.selectedItems.length) {
        return;
      }

      const selectedUsers = this.selectedItems
        .map((item) => this.resolveSelectedTableItem(item))
        .filter(Boolean);

      if (!selectedUsers.length) {
        this.$toast.warning("Secilen kullanicilar tablo verisinde bulunamadi.");
        this.selectedItems = [];
        return;
      }

      const existingIds = new Set(this.rawTempSelectedUserIds);
      const initialCount = this.tempSelectedItems.length;

      selectedUsers.forEach((item) => {
        if (!existingIds.has(item.id)) {
          this.tempSelectedItems.push(this.normalizeTempUser(item));
          existingIds.add(item.id);
        }
      });

      const addedCount = this.tempSelectedItems.length - initialCount;

      if (addedCount > 0) {
        this.$toast.success(`${addedCount} kullanici listeye eklendi.`);
      } else {
        this.$toast.warning("Secili kullanicilar zaten listede.");
      }

      this.selectedItems = [];
      this.scheduleValidation();
    },
  },
};
</script>

<style scoped>
.admin-notification-page {
  background: #f0f1fa;
  margin: -12px;
  padding: 24px;
}

.notification-page-header {
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 18px;
}

.notification-page-header h1 {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 4px;
}

.notification-page-header p {
  color: #8b8e9c;
  font-size: 13px;
  font-weight: 600;
  margin: 0;
}

.notification-page-header__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.step-card {
  background: #fff;
  border: 1px solid #d9dbea;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(32, 33, 45, 0.12);
  cursor: pointer;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
}

.step-card >>> .v-card__text {
  align-items: center;
  display: flex;
  gap: 16px;
  min-height: 76px;
  padding: 16px 20px;
}

.step-card--active {
  border: 2px solid #626ce8;
  box-shadow: 0 10px 24px rgba(72, 82, 214, 0.18);
  position: relative;
}

.step-card--active::before {
  display: none;
}

.step-card--active .step-card__number {
  background: #626ce8;
  color: #fff;
}

.step-card--active .step-card__title {
  color: #232631;
}

.step-card--active .step-card__text {
  color: #626ce8;
}

.step-card--completed {
  border-color: #d9dbea;
}

.step-card--completed .step-card__number {
  background: #eafff9;
  color: #22c7a6;
}

.step-card--locked {
  cursor: not-allowed;
  opacity: 0.58;
}

.step-card__number {
  align-items: center;
  background: #eef0ff;
  border-radius: 50%;
  color: #626ce8;
  display: flex;
  font-size: 18px;
  font-weight: 900;
  height: 46px;
  justify-content: center;
  min-width: 46px;
  width: 46px;
}

.step-card__title {
  color: #2b2d35;
  font-size: 17px;
  font-weight: 800;
  line-height: 1.25;
}

.step-card__text {
  color: #7c7f8c;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
}

.wizard-panel {
  width: 100%;
}

.wizard-actions {
  background: #fafafe;
  border-top: 1px solid #dedfeb;
  padding: 12px 16px;
}

.message-step-card,
.section-panel {
  overflow: hidden;
}

.message-card-header,
.section-panel-header {
  align-items: center;
  border-bottom: 1px solid #dedfeb;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 24px 28px;
}

.message-card-header__title,
.section-panel-header__title {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 6px;
}

.message-card-header__subtitle,
.section-panel-header__subtitle {
  color: #7c7f8c;
  font-size: 14px;
  font-weight: 700;
}

.section-panel-header__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

.delivery-context-chip {
  align-items: center;
  background: #eef0ff;
  border: 1px solid #d7dafb;
  border-radius: 4px;
  color: #626ce8;
  display: flex;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 800;
  gap: 8px;
  padding: 8px 10px;
}

.delivery-context-chip .v-icon {
  color: #626ce8;
}

.message-card-body {
  padding: 24px 28px 28px;
}

.message-form-grid {
  margin: -10px;
}

.message-form-grid__field {
  padding: 10px;
}

.message-form-grid__field:first-child {
  padding-bottom: 16px;
}

.provider-credit {
  align-items: center;
  color: #7c7f8c;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  margin-top: 8px;
}

.provider-credit--ok,
.provider-credit--ok .v-icon {
  color: #22c7a6;
}

.provider-credit--warn,
.provider-credit--warn .v-icon {
  color: #ff9d00;
}

.message-insights {
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  margin-top: 10px;
}

.message-insight {
  align-items: flex-start;
  background: #fff;
  border: 1px solid #dedfeb;
  border-left: 4px solid #626ce8;
  border-radius: 4px;
  display: flex;
  gap: 8px;
  padding: 10px 12px;
}

.message-insight--warn {
  background: #fff7e8;
  border-color: #ffd891;
  border-left-color: #ff9d00;
}

.message-insight--info .v-icon {
  color: #626ce8;
}

.message-insight--warn .v-icon {
  color: #ff9d00;
}

.message-insight strong {
  color: #2b2d35;
  display: block;
  font-size: 12px;
  font-weight: 800;
  margin-bottom: 2px;
}

.message-insight span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.section-label {
  color: #767986;
  display: block;
  font-size: 14px;
  font-weight: 800;
  margin-bottom: 10px;
}

.channel-toggle,
.scope-toggle {
  flex-wrap: wrap;
  gap: 8px;
}

.channel-toggle >>> .v-btn,
.scope-toggle >>> .v-btn {
  border: 1px solid #dedfeb;
  border-radius: 22px !important;
  color: #686b78;
  font-size: 13px;
  font-weight: 800;
  margin: 0;
  min-height: 36px;
  padding: 0 16px;
}

.channel-toggle >>> .v-btn.v-item--active,
.scope-toggle >>> .v-btn.v-item--active {
  background: #eef0ff !important;
  border-color: #626ce8;
  color: #626ce8;
}

.target-builder {
  margin-left: -16px;
  margin-right: -16px;
}

.target-panel-body {
  padding-bottom: 0;
  padding-top: 0;
}

.target-builder__row {
  border-bottom: 1px solid #dedfeb;
  display: grid;
  grid-template-columns: 170px minmax(0, 1fr);
  min-height: 72px;
}

.target-builder__row:last-child {
  border-bottom: 0;
}

.target-builder__label {
  background: #fafafe;
  border-right: 1px solid #dedfeb;
  padding: 14px 16px;
}

.target-builder__label strong {
  color: #737684;
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.target-builder__label span {
  color: #a0a3b1;
  font-size: 12px;
}

.target-builder__control {
  min-width: 0;
  padding: 14px 16px;
}

.target-note {
  border-left: 4px solid #b9bdf0;
  color: #777a87;
  font-size: 12px;
  margin-top: 10px;
  padding: 9px 11px;
}

.criteria-row {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.criteria-empty {
  background: #fafafe;
  border: 1px dashed #cfd2e4;
  border-radius: 4px;
  color: #7c7f8c;
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  padding: 12px;
}

.selected-criteria {
  display: grid;
  flex: 1;
  gap: 8px;
}

.selected-criterion,
.criterion-option {
  align-items: center;
  background: #fff;
  border: 1px solid #dedfeb;
  border-left: 4px solid #626ce8;
  border-radius: 4px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 10px 12px;
}

.selected-criterion strong,
.criterion-option strong {
  color: #2b2d35;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 3px;
}

.selected-criterion span,
.criterion-option span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
}

.criteria-drawer {
  z-index: 210;
}

.criteria-drawer__header {
  align-items: center;
  border-bottom: 1px solid #dedfeb;
  display: flex;
  justify-content: space-between;
  padding: 18px 20px;
}

.criteria-drawer__header strong {
  color: #2b2d35;
  display: block;
  font-size: 18px;
  font-weight: 800;
}

.criteria-drawer__header span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 700;
  margin-top: 3px;
}

.criteria-drawer__body {
  padding: 18px 20px;
}

.criteria-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 12px;
  margin-bottom: 14px;
}

.criteria-category--active {
  background: #eef0ff !important;
  color: #626ce8 !important;
}

.criteria-list {
  display: grid;
  gap: 10px;
}

.criterion-option__action--selected.v-btn.v-btn--disabled {
  color: #20a876 !important;
  opacity: 1;
}

.criterion-option--selected {
  border-left-color: #20a876;
}

.recipient-paths {
  display: grid;
  gap: 8px;
}

.recipient-path {
  align-items: center;
  background: #fff;
  border: 1px solid #dedfeb;
  border-left: 4px solid #626ce8;
  border-radius: 4px;
  display: grid;
  gap: 10px;
  grid-template-columns: 34px minmax(0, 1fr) 160px 160px;
  padding: 10px;
}

.recipient-path--resident {
  border-left-color: #22c7a6;
}

.recipient-path--company {
  border-left-color: #ffb22e;
}

.recipient-path__body strong {
  display: block;
  font-size: 13px;
  margin-bottom: 2px;
}

.recipient-path__body span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
}

.target-actions {
  align-items: center;
  background: #fafafe;
  border-top: 1px solid #dedfeb;
  display: flex;
  justify-content: space-between;
}

.role-summary {
  color: #2b2d35;
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.recipient-table >>> tbody td {
  padding-bottom: 10px !important;
  padding-top: 10px !important;
}

.recipient-name-cell {
  display: grid;
  gap: 3px;
  max-width: 420px;
}

.recipient-name-cell strong {
  color: #2b2d35;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
}

.recipient-name-cell span {
  color: #7c7f8c;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.send-control-card {
  position: static;
}

.control-panel-body {
  padding: 18px 18px 0;
}

.control-final-summary {
  border: 1px solid #dedfeb;
  border-left: 5px solid #aeb1bf;
  border-radius: 4px;
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(0, 1fr) auto;
  margin-bottom: 14px;
  padding: 18px 20px;
}

.control-final-summary--ok {
  border-left-color: #22c7a6;
}

.control-final-summary--warn {
  border-left-color: #ff9d00;
}

.control-final-summary--pending {
  border-left-color: #626ce8;
}

.control-final-summary h3 {
  color: #2b2d35;
  font-size: 20px;
  font-weight: 800;
  line-height: 1.25;
  margin: 0 0 6px;
}

.control-final-summary p {
  color: #7c7f8c;
  font-size: 14px;
  font-weight: 700;
  margin: 0;
}

.control-final-count {
  align-self: center;
  color: #22c7a6;
  font-size: 32px;
  font-weight: 900;
  line-height: 1;
  text-align: right;
  white-space: nowrap;
}

.control-final-summary--warn .control-final-count {
  color: #ff9d00;
}

.control-final-summary--pending .control-final-count {
  color: #626ce8;
}

.control-final-count span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-top: 6px;
  text-transform: uppercase;
}

.control-delivery-context {
  border: 1px solid #dedfeb;
  border-radius: 4px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  margin-bottom: 14px;
  overflow: hidden;
}

.control-delivery-context > div {
  border-right: 1px solid #dedfeb;
  padding: 12px 14px;
}

.control-delivery-context > div:last-child {
  border-right: 0;
}

.control-delivery-context span {
  color: #7c7f8c;
  display: block;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.control-delivery-context strong {
  color: #2b2d35;
  display: block;
  font-size: 14px;
  font-weight: 900;
  line-height: 1.25;
}

.control-state-strip {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1.4fr) minmax(300px, 0.6fr);
  margin-bottom: 14px;
}

.control-state-card {
  align-items: center;
  background: #fff;
  border: 1px solid #dedfeb;
  border-left: 4px solid #626ce8;
  border-radius: 4px;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  min-height: 66px;
  padding: 13px 14px;
}

.control-state-card--ok {
  border-left-color: #22c7a6;
}

.control-state-card--warn {
  background: #fff7e8;
  border-color: #ffd891;
  border-left-color: #ff9d00;
}

.control-state-card strong {
  color: #2b2d35;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 3px;
}

.control-state-card span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.control-state-card--warn span {
  color: #8a6418;
}

.control-dedupe-switch {
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
}

.control-stat-grid {
  border: 1px solid #dedfeb;
  border-radius: 4px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 14px;
  overflow: hidden;
}

.control-stat {
  border-right: 1px solid #dedfeb;
  padding: 14px 16px;
}

.control-stat:last-child {
  border-right: 0;
}

.control-stat span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.control-stat strong {
  color: #2b2d35;
  display: block;
  font-size: 22px;
  font-weight: 900;
  line-height: 1;
}

.control-stat--out strong {
  color: #ff9d00;
}

.control-stat--final strong {
  color: #22c7a6;
}

.control-simple-checks {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-bottom: 14px;
}

.control-simple-check {
  background: #fff;
  border: 1px solid #dedfeb;
  border-left: 4px solid #22c7a6;
  border-radius: 4px;
  padding: 12px 14px;
}

.control-simple-check--warn {
  border-left-color: #ff9d00;
}

.control-simple-check strong {
  color: #2b2d35;
  display: block;
  font-size: 13px;
  font-weight: 800;
  margin-bottom: 3px;
}

.control-simple-check span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;
}

.control-list-note {
  align-items: center;
  background: #eef0ff;
  border-radius: 4px;
  color: #626ce8;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 13px 16px;
}

.control-list-note strong {
  font-weight: 900;
}

.control-list-note__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 8px;
}

.control-list-note__actions .v-btn {
  color: #626ce8;
}

.control-chip-list {
  margin-bottom: 12px;
}

.summary-metric {
  border-bottom: 1px solid #dedfeb;
  color: #7c7f8c;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 10px;
  justify-content: space-between;
  padding: 10px 0;
}

.summary-metric:first-child {
  padding-top: 0;
}

.summary-metric strong {
  color: #2b2d35;
  font-size: 15px;
  text-align: right;
}

.summary-metric__good {
  color: #22c7a6 !important;
}

.summary-metric__warn {
  color: #ff9d00 !important;
}

.summary-metric__bad {
  color: #ff5570 !important;
}

.checklist {
  display: grid;
  gap: 8px;
}

.checklist__item {
  background: #fff;
  border: 1px solid #dedfeb;
  border-radius: 4px;
  display: grid;
  gap: 8px;
  grid-template-columns: 22px minmax(0, 1fr);
  padding: 9px;
}

.checklist__item .v-icon {
  align-items: center;
  border-radius: 50%;
  display: flex;
  height: 22px;
  justify-content: center;
  width: 22px;
}

.checklist__item strong {
  display: block;
  font-size: 12px;
}

.checklist__item span {
  color: #7c7f8c;
  display: block;
  font-size: 12px;
}

.checklist__item--ok .v-icon {
  background: #eafff9;
  color: #22c7a6;
}

.checklist__item--warn .v-icon {
  background: #fff7e8;
  color: #ff9d00;
}

@media (max-width: 960px) {
  .message-card-header,
  .section-panel-header {
    align-items: flex-start;
    display: block;
    padding: 20px;
  }

  .message-card-header .v-btn,
  .section-panel-header .v-btn {
    margin-top: 10px;
  }

  .section-panel-header__actions {
    justify-content: flex-start;
    margin-top: 10px;
  }

  .delivery-context-chip {
    display: inline-flex;
    margin-top: 10px;
  }

  .message-card-body {
    padding: 20px;
  }

  .notification-page-header {
    align-items: flex-start;
    display: block;
  }

  .notification-page-header__actions {
    justify-content: flex-start;
    margin-top: 12px;
  }

  .target-builder__row {
    grid-template-columns: 1fr;
  }

  .target-builder__label {
    border-bottom: 1px solid #dedfeb;
    border-right: 0;
  }

  .recipient-path {
    grid-template-columns: 34px minmax(0, 1fr);
  }

  .target-actions {
    align-items: flex-start;
    display: block;
  }

  .control-final-summary,
  .control-state-strip,
  .control-stat-grid,
  .control-simple-checks {
    grid-template-columns: 1fr;
  }

  .control-final-count {
    text-align: left;
  }

  .control-state-card,
  .control-list-note {
    align-items: flex-start;
    display: grid;
  }

  .control-list-note__actions {
    justify-content: flex-start;
  }

  .control-stat {
    border-bottom: 1px solid #dedfeb;
    border-right: 0;
  }

  .control-stat:last-child {
    border-bottom: 0;
  }
}
</style>
